import React, { useState, useRef, useEffect, useReducer } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/AuthContext';
import { useReaderCredits } from '@/contexts/ReaderCreditContext';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';
import { useNotification } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { playNotificationSound, playUrgentNotification } from '@/utils/audioNotification';
import './RetroControls.css';
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  X,
  Home,
  Loader2,
  SkipBack,
  SkipForward,
  ShoppingCart,
  ExternalLink,
  Menu,
  Volume2,
  Share2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AudiaChatModal from './AudiaChatModal';
import RegistrationModal from './RegistrationModal';
import BookRating from './BookRating';
import PlayerCoverZoom from './PlayerCoverZoom';
import { BACKEND_URL } from '@/config/api';
import { PREVIEW_PROMO_CODE } from '@/constants/appConstants';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

// =============================================================================
// P4.1: PLAYBACK LATENCY INSTRUMENTATION
// =============================================================================
// Captures timing metrics for audio playback performance analysis:
// - t_click_play: When user initiates playback
// - t_audio_request_start: When audio fetch begins
// - t_canplay: When audio element reports canplaythrough
// - t_playing: When audio actually starts playing
// - TTFP (Time To First Playback): t_playing - t_click_play

const createPerformanceTracker = () => {
  let metrics = {
    manuscriptId: null,
    segmentId: null,
    chunkIndex: null,
    t_click_play: null,
    t_audio_request_start: null,
    t_canplay: null,
    t_playing: null,
    t_first_timeupdate: null,
    network_type: null,
    from_cache: false,
  };
  
  return {
    startPlayRequest: (manuscriptId, segmentId, chunkIndex) => {
      metrics = {
        manuscriptId,
        segmentId,
        chunkIndex,
        t_click_play: performance.now(),
        t_audio_request_start: null,
        t_canplay: null,
        t_playing: null,
        t_first_timeupdate: null,
        network_type: navigator?.connection?.effectiveType || 'unknown',
        from_cache: false,
      };
      console.log('[PERF] play_request_start', { manuscriptId, segmentId, chunkIndex });
    },
    
    markAudioRequestStart: () => {
      metrics.t_audio_request_start = performance.now();
    },
    
    markCanPlay: () => {
      metrics.t_canplay = performance.now();
    },
    
    markPlaying: () => {
      metrics.t_playing = performance.now();
      // Log the complete metrics
      const TTFP = metrics.t_playing - metrics.t_click_play;
      const network_ms = metrics.t_canplay ? metrics.t_canplay - (metrics.t_audio_request_start || metrics.t_click_play) : 0;
      const buffer_ms = metrics.t_canplay ? metrics.t_playing - metrics.t_canplay : 0;
      
      console.log(
        `[PERF] play_start manuscript=${metrics.manuscriptId} segment=${metrics.segmentId} ` +
        `chunk=${metrics.chunkIndex} TTFP_ms=${TTFP.toFixed(0)} net_ms=${network_ms.toFixed(0)} ` +
        `buffer_ms=${buffer_ms.toFixed(0)} network=${metrics.network_type} cached=${metrics.from_cache}`
      );
      
      return { TTFP, network_ms, buffer_ms };
    },
    
    markCached: () => {
      metrics.from_cache = true;
    },
    
    getMetrics: () => ({ ...metrics }),
  };
};

// =============================================================================
// P4.2: AUDIO SEGMENT PREFETCH CACHE
// =============================================================================
// LRU cache for prefetched audio segments:
// - Max 2 segments (current + next)
// - Keyed by (manuscript_id, segment_id, chunk_index)
// - Stores blob URLs for instant playback

const createAudioPrefetchCache = () => {
  const cache = new Map(); // key -> { blobUrl, timestamp, size }
  const MAX_CACHE_SIZE = 2;
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  
  const getCacheKey = (manuscriptId, segmentIndex, chunkIndex) => 
    `${manuscriptId}:${segmentIndex}:${chunkIndex}`;
  
  return {
    get: (manuscriptId, segmentIndex, chunkIndex) => {
      const key = getCacheKey(manuscriptId, segmentIndex, chunkIndex);
      const entry = cache.get(key);
      
      if (entry) {
        // Check TTL
        if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
          console.log('[PREFETCH] Cache expired:', key);
          URL.revokeObjectURL(entry.blobUrl);
          cache.delete(key);
          return null;
        }
        console.log('[PREFETCH] Cache HIT:', key);
        return entry.blobUrl;
      }
      console.log('[PREFETCH] Cache MISS:', key);
      return null;
    },
    
    set: (manuscriptId, segmentIndex, chunkIndex, blobUrl, size = 0) => {
      const key = getCacheKey(manuscriptId, segmentIndex, chunkIndex);
      
      // Evict oldest if at capacity
      if (cache.size >= MAX_CACHE_SIZE) {
        let oldestKey = null;
        let oldestTime = Infinity;
        for (const [k, v] of cache.entries()) {
          if (v.timestamp < oldestTime) {
            oldestTime = v.timestamp;
            oldestKey = k;
          }
        }
        if (oldestKey) {
          const old = cache.get(oldestKey);
          URL.revokeObjectURL(old.blobUrl);
          cache.delete(oldestKey);
          console.log('[PREFETCH] Evicted from cache:', oldestKey);
        }
      }
      
      cache.set(key, { blobUrl, timestamp: Date.now(), size });
      console.log('[PREFETCH] Added to cache:', key, `(${(size/1024).toFixed(1)}KB)`);
    },
    
    has: (manuscriptId, segmentIndex, chunkIndex) => {
      const key = getCacheKey(manuscriptId, segmentIndex, chunkIndex);
      return cache.has(key);
    },
    
    clear: () => {
      for (const entry of cache.values()) {
        URL.revokeObjectURL(entry.blobUrl);
      }
      cache.clear();
      console.log('[PREFETCH] Cache cleared');
    },
    
    size: () => cache.size,
  };
};

// Global instances
const perfTracker = createPerformanceTracker();
const audioCache = createAudioPrefetchCache();

// Player state reducer for atomic state transitions
const playerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_AUDIO_SOURCE':
      // P6: Validate audio source - must be a valid audio URL, not a page URL
      const audioSrc = action.payload.src;
      const isValidAudioSrc = audioSrc && (
        audioSrc.includes('/audio/') ||
        audioSrc.includes('.mp3') ||
        audioSrc.includes('.wav') ||
        audioSrc.includes('.m4a') ||
        audioSrc.includes('audio_url') ||
        audioSrc.includes('/api/') // Backend audio endpoints
      );
      
      if (!isValidAudioSrc) {
        console.error('[REDUCER] REJECTED invalid audio source:', audioSrc);
        return state; // Don't update with invalid source
      }
      
      console.log('[REDUCER] SET_AUDIO_SOURCE dispatched:', {
        chapterIndex: action.payload.chapterIndex,
        chunkIndex: action.payload.chunkIndex,
        numChunks: action.payload.chunks?.length,
        chapterLabel: action.payload.chapterLabel
      });
      
      return {
        ...state,
        currentAudioSrc: action.payload.src,
        audioChunks: action.payload.chunks,
        currentChunkIndex: action.payload.chunkIndex,
        currentChapterIndex: action.payload.chapterIndex,
        currentChapter: action.payload.chapterLabel,
        realtimeCharIndex: action.payload.charIndex,
        progressPercent: action.payload.progress,
        shouldPlay: action.payload.shouldPlay || false,
      };
    case 'UPDATE_PLAYBACK_STATE':
      return {
        ...state,
        isPlaying: action.payload.isPlaying,
      };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        realtimeCharIndex: action.payload.charIndex,
        progressPercent: action.payload.progress,
      };
    case 'NEXT_CHUNK':
      const nextChunk = state.audioChunks[action.payload.nextChunkIndex];
      return {
        ...state,
        currentChunkIndex: action.payload.nextChunkIndex,
        currentAudioSrc: `${BACKEND_URL}${nextChunk.audio_url}`,
        realtimeCharIndex: nextChunk.start_char || 0,
        shouldPlay: false,
      };
    case 'CLEAR_AUDIO':
      return {
        ...state,
        currentAudioSrc: null,
        isPlaying: false,
        shouldPlay: false,
      };
    default:
      return state;
  }
};

const initialPlayerState = {
  currentAudioSrc: null,
  audioChunks: [],
  currentChunkIndex: 0,
  currentChapterIndex: 0,
  currentChapter: 'Segment 1',
  isPlaying: false,
  realtimeCharIndex: 0,
  progressPercent: 0,
  shouldPlay: false,
};

const ManuscriptPlayer = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshCreditsSummary, balance, optimisticDebit, updateBalanceFromResponse } = useReaderCredits();
  const { setShowCreditExpiredModal, setShowPricingModal } = useReaderFunnel();
  const { notifyBilling } = useNotification();
  
  // PREVIEW MODE DETECTION - Critical for credit bypass and creator endpoint routing
  // Preview mode is triggered by:
  // 1. Route includes '/simple-ingest/' (launched from Simple Ingest preview)
  // 2. Location state has preview: true
  // 3. Session has is_preview: true or session_type: 'simple_ingest_preview'
  const [isPreviewMode, setIsPreviewMode] = useState(() => {
    // Check URL path
    if (location.pathname.includes('/simple-ingest/')) return true;
    // Check return path
    if (location.state?.returnTo?.includes('/simple-ingest/')) return true;
    // Check explicit preview flag
    if (location.state?.preview === true) return true;
    return false;
  });
  
  // SAMPLE PREVIEW MODE - Author previewing their book sample
  // No credits debited, no analytics, only sample segments loaded
  const [isSamplePreviewMode, setIsSamplePreviewMode] = useState(() => {
    return location.state?.samplePreview === true;
  });
  
  // GUEST SAMPLE MODE - Unauthenticated user playing a book sample
  // No credits, no bookmarking, shows end-of-sample CTA
  const [isGuestSampleMode, setIsGuestSampleMode] = useState(() => {
    return location.state?.guestSample === true;
  });
  const [anonymousId] = useState(() => location.state?.anonymousId || null);
  
  // PROMOTIONAL MODE - Full book playback for promotional titles (from landing page)
  // No credits charged, no commissions, full chapter access, registration CTA on close
  const [isPromotionalMode, setIsPromotionalMode] = useState(() => {
    return location.state?.isPromotional === true;
  });
  
  // RADIO MODE - Continuous sample playback across multiple titles (from landing page radio)
  const [isRadioMode] = useState(() => location.state?.radioMode || false);
  
  // State for guest sample completion CTA
  const [showGuestSampleCTA, setShowGuestSampleCTA] = useState(false);
  
  // State for radio "next track" modal
  const [showRadioNextModal, setShowRadioNextModal] = useState(false);
  
  // State for registration modal (opened from guest CTA)
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  
  // Preview conversion: question counter and bubble state (guest sample mode only)
  const [previewQuestionCount, setPreviewQuestionCount] = useState(0);
  const [showConversionBubble, setShowConversionBubble] = useState(false);
  const [registrationPromoCode, setRegistrationPromoCode] = useState(null);
  
  // Smart default based on user role
  const getDefaultReturnPath = () => {
    if (location.state?.returnTo) {
      return location.state.returnTo;
    }
    
    // Check if user is authenticated
    if (!user) {
      // Unauthenticated visitors return to Landing Page
      return '/';
    }
    
    // Default to My Library for authenticated users with reader role
    if (user.roles?.includes('reader')) {
      return '/reader/my-library';
    }
    
    // Default to author home for authors (handles both "author" and legacy "creator")
    if (user.roles?.includes('author') || user.roles?.includes('creator')) {
      return '/author';
    }
    
    // Fallback for authenticated users without specific role
    return '/';
  };
  
  const returnTo = getDefaultReturnPath();
  
  // Debug: Log returnTo on mount
  React.useEffect(() => {
    console.log('[ManuscriptPlayer] Loaded with returnTo:', returnTo, 'state:', location.state);
    console.log('[ManuscriptPlayer] User:', user);
    console.log('[ManuscriptPlayer] Preview mode:', isPreviewMode);
    
    // CRITICAL: Capture the initial navigation state immediately on mount
    // This prevents the state from being lost due to React Router navigation updates
    if (!initialLocationStateRef.current && location.state) {
      initialLocationStateRef.current = { ...location.state };
      console.log('[ManuscriptPlayer] Captured initial location state:', initialLocationStateRef.current);
    }
  }, []);
  
  const [session, setSession] = useState(null);
  const [manuscript, setManuscript] = useState(null);
  const [outline, setOutline] = useState(null);
  const [audioManifest, setAudioManifest] = useState(null); // P0: Audio manifest for v3 manuscripts
  const [loading, setLoading] = useState(true);
  const [commandLoading, setCommandLoading] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [showChapterMenu, setShowChapterMenu] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Referral flow state
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [showReferralConfirm, setShowReferralConfirm] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');
  const wasPlayingBeforeReferral = useRef(false);
  const [chapterCount, setChapterCount] = useState(1);
  
  // Hard Bookmark state
  const [resumeTimeSeconds, setResumeTimeSeconds] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [loadedBookmarkDetails, setLoadedBookmarkDetails] = useState(null);
  
  // Mobile autoplay state - show play button when autoplay blocked by browser
  const [showMobilePlayPrompt, setShowMobilePlayPrompt] = useState(false);
  const pendingAutoplayRef = useRef(false); // Track if we have a pending autoplay request
  
  // Audia Chat state
  const [isAudiaChatOpen, setIsAudiaChatOpen] = useState(false);
  
  // Recap ("Catch Me Up") state
  const [showRecapPrompt, setShowRecapPrompt] = useState(false);
  const [recapSkipped, setRecapSkipped] = useState(false); // P4 fix: Prevent recap modal from re-appearing
  const [recapLoading, setRecapLoading] = useState(false);
  const [recapText, setRecapText] = useState("");
  const [recapAudioUrl, setRecapAudioUrl] = useState(null);
  const recapAudioRef = useRef(null);
  const recapCheckDoneRef = useRef(false); // Prevent duplicate recap checks on re-renders
  const bookmarkLoadStartedRef = useRef(false); // Prevent duplicate loadHardBookmark calls
  const initialLocationStateRef = useRef(null); // Capture initial navigation state
  
  // Engagement tracking
  const [sessionListenSeconds, setSessionListenSeconds] = useState(0);
  const lastTimeUpdateRef = useRef(null);
  
  // Show conversion bubble when guest sample mode activates
  useEffect(() => {
    if (isGuestSampleMode) {
      setShowConversionBubble(true);
    }
    return () => {
      // Reset preview state on unmount
      setPreviewQuestionCount(0);
      setShowConversionBubble(false);
    };
  }, [isGuestSampleMode]);
  
  // Use reducer for all player state
  const [playerState, dispatch] = useReducer(playerReducer, initialPlayerState);
  
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const playTriggerRef = useRef(0);
  const playerContainerRef = useRef(null); // For focus management
  
  // Track current position synchronously with refs (not state)
  const currentChapterRef = useRef(0);
  const currentChunkRef = useRef(0);
  const lastValidTimeRef = useRef(0); // Store last valid audio currentTime from handleTimeUpdate
  
  // P5: Single-flight playback guard to prevent race conditions
  const playbackGuardRef = useRef(0);
  const pendingSeekCleanupRef = useRef(null); // Store cleanup function for pending seek listeners
  const lastLoadedSrcRef = useRef(null); // Track last loaded src to avoid redundant load() calls
  const playbackInProgressRef = useRef(false); // Track if playback is currently being initiated
  
  // P5: Kill switch for gated seek behavior - can be toggled OFF to revert to legacy if issues arise
  // Set to 'false' to disable metadata-gated seek and use legacy immediate-seek behavior
  const PLAYER_GATED_SEEK_ENABLED = true; // ROLLBACK: Set to false if gated seek causes issues
  
  // Sync refs whenever playerState changes
  useEffect(() => {
    currentChapterRef.current = playerState.currentChapterIndex;
    currentChunkRef.current = playerState.currentChunkIndex;
  }, [playerState.currentChapterIndex, playerState.currentChunkIndex]);

  // ============================================================================
  // CREDIT POLLING: Check balance during playback, auto-stop at 0
  // IMPORTANT: Completely bypassed in Preview Mode - no credits assessed
  // ============================================================================
  const creditPollIntervalRef = useRef(null);
  const lastCreditCheckRef = useRef(Date.now());
  
  // Poll credits every 30 seconds during playback
  useEffect(() => {
    // PREVIEW MODE BYPASS: No credit polling in preview, sample preview, guest sample, or promotional mode
    if (isPreviewMode || isSamplePreviewMode || isGuestSampleMode || isPromotionalMode) {
      console.log('[CREDITS POLL] Preview/Sample/Guest/Promotional mode - credit polling DISABLED');
      return;
    }
    
    const POLL_INTERVAL_MS = 30000; // 30 seconds
    
    const checkCreditsAndStop = async () => {
      // Only poll if playing
      if (!playerState.isPlaying) return;
      
      // Avoid polling too frequently
      if (Date.now() - lastCreditCheckRef.current < POLL_INTERVAL_MS - 1000) return;
      lastCreditCheckRef.current = Date.now();
      
      console.log('[CREDITS POLL] Checking balance during playback...');
      const result = await refreshCreditsSummary(1);
      
      if (result && typeof result.balance === 'number') {
        console.log('[CREDITS POLL] Balance:', result.balance);
        
        // If balance is 0 or less, stop playback and show modal
        if (result.balance <= 0) {
          console.log('[CREDITS POLL] Balance exhausted! Stopping playback...');
          
          // Stop audio playback
          if (audioRef.current) {
            audioRef.current.pause();
          }
          dispatch({ type: 'SET_PLAYING', payload: false });
          
          // Show branded modal notification
          notifyBilling('Your Reader Credits have run out. Please purchase credits to continue listening.');
          
          // Show pricing modal
          setShowPricingModal(true);
        }
      }
    };
    
    // Set up polling interval
    if (playerState.isPlaying) {
      // Immediate check when playback starts
      checkCreditsAndStop();
      
      creditPollIntervalRef.current = setInterval(checkCreditsAndStop, POLL_INTERVAL_MS);
      console.log('[CREDITS POLL] Started polling during playback');
    } else {
      // Clear interval when not playing
      if (creditPollIntervalRef.current) {
        clearInterval(creditPollIntervalRef.current);
        creditPollIntervalRef.current = null;
        console.log('[CREDITS POLL] Stopped polling');
      }
    }
    
    return () => {
      if (creditPollIntervalRef.current) {
        clearInterval(creditPollIntervalRef.current);
        creditPollIntervalRef.current = null;
      }
    };
  }, [playerState.isPlaying, refreshCreditsSummary, setShowPricingModal, notifyBilling]);
  
  // Also check credits when balance prop changes
  useEffect(() => {
    if (typeof balance === 'number' && balance <= 0 && playerState.isPlaying) {
      console.log('[CREDITS] Balance dropped to 0 during playback, stopping...');
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      dispatch({ type: 'SET_PLAYING', payload: false });
      
      notifyBilling('Your Reader Credits have run out. Please purchase credits to continue listening.');
      
      setShowPricingModal(true);
    }
  }, [balance, playerState.isPlaying, setShowPricingModal, notifyBilling]);

  // P4.2: Prefetch next chunk when current chunk starts playing
  const prefetchNextChunk = async (chunks, currentChunkIndex, manuscriptId, chapterIndex) => {
    const nextChunkIndex = currentChunkIndex + 1;
    if (nextChunkIndex >= chunks.length) {
      console.log('[PREFETCH] No next chunk to prefetch (end of chapter)');
      return;
    }
    
    // Check if already cached
    if (audioCache.has(manuscriptId, chapterIndex, nextChunkIndex)) {
      console.log('[PREFETCH] Next chunk already cached');
      return;
    }
    
    const nextChunk = chunks[nextChunkIndex];
    const audioUrl = `${BACKEND_URL}${nextChunk.audio_url}`;
    
    console.log('[PREFETCH] Starting prefetch of next chunk:', { 
      chunkIndex: nextChunkIndex, 
      url: audioUrl 
    });
    
    try {
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      audioCache.set(manuscriptId, chapterIndex, nextChunkIndex, blobUrl, blob.size);
      console.log('[PREFETCH] ✓ Next chunk prefetched successfully');
    } catch (error) {
      console.warn('[PREFETCH] Failed to prefetch next chunk:', error.message);
      // Non-fatal - playback will still work via normal fetch
    }
  };

  useEffect(() => {
    loadSession();
  }, [sessionId]);
  
  // P4: Cleanup cache on unmount
  useEffect(() => {
    return () => {
      audioCache.clear();
    };
  }, []);
  
  // P6: Proper audio cleanup on unmount to prevent "Invalid audio source" errors
  // This ensures audio element is properly stopped before navigation
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        console.log('[CLEANUP] Stopping audio on unmount');
        audio.pause();
        audio.removeAttribute('src');
        audio.load(); // Force detach from source
      }
      // Clear any pending seeks
      if (pendingSeekCleanupRef.current) {
        pendingSeekCleanupRef.current();
        pendingSeekCleanupRef.current = null;
      }
    };
  }, []);
  
  // Helper function to format seconds into MM:SS format
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to trigger audio playback without causing re-render loops
  // seekToTime: optional parameter to seek to a specific time before playing
  // P4.1: Instrumented with performance tracking
  // P5: Fixed race condition - metadata-gated seek + single-flight guard
  // CONDITION B: Kill switch via PLAYER_GATED_SEEK_ENABLED
  const playAudioNow = async (audioSrc, seekToTime = null, options = {}) => {
    if (!audioSrc || !audioRef.current) return;
    
    // CREDIT GATE: Block playback if balance is 0
    // BYPASS for preview, sample preview, and guest sample modes
    if (!isPreviewMode && !isSamplePreviewMode && !isGuestSampleMode && typeof balance === 'number' && balance <= 0) {
      console.log('[PLAYBACK] BLOCKED: No credits remaining (balance:', balance, ')');
      notifyBilling('You need Reader Credits to listen. Please purchase credits to continue.');
      setShowPricingModal(true);
      return;
    }
    
    const { fromCache = false, manuscriptId = manuscript?.id, chapterIndex = playerState.currentChapterIndex, chunkIndex = playerState.currentChunkIndex } = options;
    
    // P5 ENHANCED: Check if playback is already in progress to prevent stutter
    if (playbackInProgressRef.current) {
      console.log('[PLAYBACK] Playback already in progress, ignoring duplicate request');
      return;
    }
    
    // P5: Single-flight guard - increment and capture current invocation ID
    const currentInvocation = ++playbackGuardRef.current;
    playbackInProgressRef.current = true;
    
    // P5: Clean up any pending seek listeners from previous invocation
    if (pendingSeekCleanupRef.current) {
      pendingSeekCleanupRef.current();
      pendingSeekCleanupRef.current = null;
    }
    
    // P4.1: Mark audio request start
    perfTracker.markAudioRequestStart();
    if (fromCache) perfTracker.markCached();
    
    console.log('[PLAYBACK] Starting audio:', {
      src: audioSrc,
      chapterIndex: playerState.currentChapterIndex,
      chapterLabel: playerState.currentChapter,
      seekToTime: seekToTime,
      fromCache: fromCache,
      invocation: currentInvocation,
      gatedSeekEnabled: PLAYER_GATED_SEEK_ENABLED
    });
    
    try {
      const audio = audioRef.current;
      
      // CONDITION A: Only call load() when src actually changes
      // This prevents redundant buffering when resuming from same source.
      // load() is required when src changes to:
      //   1. Reset readyState to 0 (prevents stale state from previous audio)
      //   2. Abort any pending network requests from old src
      //   3. Ensure loadedmetadata fires for the NEW source
      // Tested in: Chrome, Edge, Firefox. Safari may auto-load on src change.
      const srcChanged = lastLoadedSrcRef.current !== audioSrc;
      audio.src = audioSrc;
      
      if (srcChanged) {
        audio.load(); // Only load when source actually changes
        lastLoadedSrcRef.current = audioSrc;
        console.log('[PLAYBACK] Source changed, called load()');
      } else {
        console.log('[PLAYBACK] Same source, skipping load()');
      }
      
      // Use passed seekToTime parameter or fall back to state
      const timeToSeek = seekToTime !== null ? seekToTime : resumeTimeSeconds;
      
      // CONDITION B: Kill switch - use legacy behavior if gated seek is disabled
      if (!PLAYER_GATED_SEEK_ENABLED) {
        // LEGACY BEHAVIOR: Immediate seek without metadata gate
        console.log('[PLAYBACK] Using legacy immediate-seek behavior (kill switch active)');
        
        if (timeToSeek !== null && timeToSeek > 0) {
          const seekAndPlay = () => {
            audio.currentTime = timeToSeek;
            audio.play().then(() => {
              dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
              setResumeTimeSeconds(null);
              playbackInProgressRef.current = false;
            }).catch((error) => {
              console.error('[PLAYBACK] Legacy play error:', error);
              dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
              playbackInProgressRef.current = false;
            });
          };
          
          if (audio.readyState >= 2) {
            seekAndPlay();
          } else {
            audio.addEventListener('canplay', seekAndPlay, { once: true });
          }
        } else {
          await audio.play();
          dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
          playbackInProgressRef.current = false;
        }
        return;
      }
      
      // NEW BEHAVIOR: Metadata-gated seek (PLAYER_GATED_SEEK_ENABLED = true)
      // Check if we need to resume from a bookmark
      if (timeToSeek !== null && timeToSeek > 0) {
        console.log('[BOOKMARK] Will resume from position:', timeToSeek, 'after metadata loads');
        
        // P5: Create a promise that resolves when seek and play complete
        await new Promise((resolve, reject) => {
          const onLoadedMetadata = () => {
            // P5: Check if this invocation is still current
            if (playbackGuardRef.current !== currentInvocation) {
              console.log('[PLAYBACK] Invocation superseded, aborting seek');
              cleanup();
              playbackInProgressRef.current = false;
              resolve();
              return;
            }
            
            console.log('[BOOKMARK] Metadata loaded, seeking to:', timeToSeek);
            console.log('[BOOKMARK] Audio duration:', audio.duration, 'readyState:', audio.readyState);
            
            // Now safe to seek
            audio.currentTime = timeToSeek;
            console.log('[BOOKMARK] After seek, currentTime is:', audio.currentTime);
            
            // Wait for seeked event before playing
            const onSeeked = () => {
              if (playbackGuardRef.current !== currentInvocation) {
                cleanup();
                playbackInProgressRef.current = false;
                resolve();
                return;
              }
              
              console.log('[BOOKMARK] Seek complete, starting playback');
              audio.play().then(() => {
                dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                console.log('[PLAYBACK] Audio playing successfully from bookmark at', timeToSeek, 'seconds');
                setResumeTimeSeconds(null); // Clear bookmark after use
                playbackInProgressRef.current = false;
                cleanup();
                resolve();
              }).catch((error) => {
                console.error('[PLAYBACK] Error playing audio:', error);
                dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
                playbackInProgressRef.current = false;
                cleanup();
                reject(error);
              });
            };
            
            audio.addEventListener('seeked', onSeeked, { once: true });
          };
          
          const onError = (e) => {
            console.error('[PLAYBACK] Audio load error:', e);
            cleanup();
            reject(new Error('Audio load failed'));
          };
          
          const cleanup = () => {
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('error', onError);
            pendingSeekCleanupRef.current = null;
          };
          
          // Store cleanup function for potential external cancellation
          pendingSeekCleanupRef.current = cleanup;
          
          // P5: Always wait for loadedmetadata - don't trust stale readyState
          audio.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
          audio.addEventListener('error', onError, { once: true });
          
          // Timeout safety net
          setTimeout(() => {
            if (playbackGuardRef.current === currentInvocation && pendingSeekCleanupRef.current) {
              console.warn('[PLAYBACK] Metadata load timeout, attempting fallback');
              cleanup();
              // Fallback: try to play anyway
              audio.play().then(() => {
                dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                playbackInProgressRef.current = false;
                resolve();
              }).catch((error) => {
                playbackInProgressRef.current = false;
                reject(error);
              });
            }
          }, 10000); // 10 second timeout
        });
      } else {
        // Normal play without bookmark - still wait for canplay
        await new Promise((resolve, reject) => {
          const onCanPlay = () => {
            if (playbackGuardRef.current !== currentInvocation) {
              cleanup();
              playbackInProgressRef.current = false;
              resolve();
              return;
            }
            
            audio.play().then(() => {
              dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
              console.log('[PLAYBACK] Audio playing successfully');
              playbackInProgressRef.current = false;
              cleanup();
              resolve();
            }).catch((error) => {
              console.error('[PLAYBACK] Error playing audio:', error);
              dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
              playbackInProgressRef.current = false;
              cleanup();
              reject(error);
            });
          };
          
          const cleanup = () => {
            audio.removeEventListener('canplay', onCanPlay);
            pendingSeekCleanupRef.current = null;
          };
          
          pendingSeekCleanupRef.current = cleanup;
          
          // Check if already ready (for same-src resume)
          if (audio.readyState >= 3) {
            onCanPlay();
          } else {
            audio.addEventListener('canplay', onCanPlay, { once: true });
          }
        });
      }
    } catch (error) {
      console.error('[PLAYBACK] Error in playAudioNow:', error);
      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
      playbackInProgressRef.current = false;
      
      // Check if this is a mobile autoplay block (NotAllowedError)
      if (error.name === 'NotAllowedError' || error.message?.includes('user gesture')) {
        console.log('[PLAYBACK] Autoplay blocked by browser - showing mobile play prompt');
        setShowMobilePlayPrompt(true);
      }
    }
  };

  // HARD BOOKMARK FUNCTIONS
  const saveHardBookmark = async (type = 'close', passedAudioTime = null) => {
    const manuscriptId = manuscript?.id;
    if (!manuscriptId) {
      console.log('[BOOKMARK] Cannot save - missing manuscriptId');
      return;
    }
    
    // Get EXACT position - read from refs which are always current
    const segmentIndex = currentChapterRef.current;
    const chunkIndex = currentChunkRef.current;
    
    // Get current audio time - use passed time if available (more reliable)
    let currentTime = 0;
    
    if (passedAudioTime !== null && passedAudioTime !== undefined && passedAudioTime > 0) {
      // Use the time that was passed in (already captured before any pausing/clearing)
      currentTime = passedAudioTime;
      console.log('[BOOKMARK] ✓ Using passed audioTime:', currentTime);
    } else if (audioRef.current && audioRef.current.currentTime > 0 && !isNaN(audioRef.current.currentTime)) {
      // Fallback 1: read from audio element directly
      currentTime = audioRef.current.currentTime;
      console.log('[BOOKMARK] Using currentTime from audioRef:', currentTime);
    } else if (lastValidTimeRef.current > 0) {
      // Fallback 2: use last valid time from handleTimeUpdate
      currentTime = lastValidTimeRef.current;
      console.log('[BOOKMARK] ✓ Using lastValidTimeRef (last known position):', currentTime);
    } else {
      console.warn('[BOOKMARK] ⚠️ No valid audio time available! Saving as 0');
    }
    
    console.log('[BOOKMARK] Final currentTime to save:', currentTime);
    console.log('[BOOKMARK] segmentIndex (chapterIndex):', segmentIndex);
    console.log('[BOOKMARK] chunkIndex:', chunkIndex);
    
    const bookmarkData = {
      type,
      manuscriptId,
      manuscriptTitle: manuscript?.title || 'Unknown',
      segmentIndex: segmentIndex,
      chunkIndex,
      positionSeconds: currentTime,
      chunkStartChar: playerState.audioChunks?.[chunkIndex]?.start_char,
      calculatedFromChunk: true,
      timestamp: new Date().toISOString()
    };
    
    console.log('[BOOKMARK] Capturing hard bookmark:', bookmarkData);
    
    // ✅ SAVE TO LOCALSTORAGE IMMEDIATELY (SINGLE SOURCE OF TRUTH)
    const token = localStorage.getItem('accessToken');
    let userId = 'anonymous';
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload.user_id || payload.sub || 'anonymous';
      } catch (e) {
        console.warn('[BOOKMARK] Could not extract user ID from token');
      }
    }
    
    const bookmarkKey = `newaudia_bookmark_${userId}_${manuscriptId}`;
    const bookmarkForStorage = {
      chapterIndex: segmentIndex,
      chunkIndex: chunkIndex,
      positionSeconds: currentTime,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(bookmarkKey, JSON.stringify(bookmarkForStorage));
    console.log('[BOOKMARK] ✓ Saved to localStorage:', bookmarkKey, bookmarkForStorage);
    
    // Save to database as backup via engagement endpoint
    if (token) {
      try {
        const last_position = {
          current_chapter_index: segmentIndex,
          current_chunk_index: chunkIndex,
          position_seconds: currentTime
        };
        
        const response = await axios.post(
          `${API}/manuscripts/${manuscriptId}/engagement`,
          {
            listen_seconds: 0,
            words_consumed: 0,
            role: location.state?.role || 'reader',
            last_position: last_position
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        console.log('[BOOKMARK] ✓ Successfully saved to database:', last_position);
        console.log('[BOOKMARK] ✓ API response:', response.data);
      } catch (err) {
        console.error('[BOOKMARK] ✗ FAILED to save to database:', err);
        console.error('[BOOKMARK] ✗ Error details:', err.response?.data || err.message);
        console.error('[BOOKMARK] ✗ Status:', err.response?.status);
      }
    }
  };

  const checkRecapEligibility = async (bookmark) => {
    // Check if we should offer "Catch Me Up" recap
    // Only offer recap if:
    // 1. User has bookmark beyond segment 0
    // 2. Last interaction was MORE than 24 hours ago
    // If last interaction was less than 24 hours, just resume from bookmark (no recap)
    
    // CRITICAL: Prevent duplicate recap checks on re-renders
    if (recapCheckDoneRef.current) {
      console.log('[RECAP ELIGIBILITY] ✗ Recap check already done this session');
      return false;
    }
    
    // If user already skipped or started recap this session, don't show again
    if (recapSkipped) {
      console.log('[RECAP ELIGIBILITY] ✗ User already interacted with recap this session');
      return false;
    }
    
    const chapterIndex = bookmark.chapterIndex || 0;
    const chunkIndex = bookmark.chunkIndex || 0;
    const bookmarkTimestamp = bookmark.timestamp;
    
    console.log('[RECAP ELIGIBILITY] Checking bookmark:', { chapterIndex, chunkIndex, timestamp: bookmarkTimestamp, bookmark });
    
    // Not at start of book (beyond beginning of segment 0)?
    if (chapterIndex > 0 || chunkIndex > 0) {
      // Check if last interaction was more than 24 hours ago
      if (bookmarkTimestamp) {
        const lastInteractionTime = new Date(bookmarkTimestamp).getTime();
        const now = Date.now();
        const hoursSinceLastInteraction = (now - lastInteractionTime) / (1000 * 60 * 60);
        
        console.log('[RECAP ELIGIBILITY] Time since last interaction:', {
          lastInteraction: bookmarkTimestamp,
          hoursSince: hoursSinceLastInteraction.toFixed(2),
          threshold: '24 hours'
        });
        
        // If less than 24 hours, skip recap and resume directly
        if (hoursSinceLastInteraction < 24) {
          console.log('[RECAP ELIGIBILITY] ✗ Last interaction was less than 24 hours ago - skipping recap, resuming directly');
          recapCheckDoneRef.current = true; // Mark as done
          return false;
        }
        
        console.log('[RECAP ELIGIBILITY] ✓ Last interaction was more than 24 hours ago - offering recap');
      } else {
        console.log('[RECAP ELIGIBILITY] ✓ No timestamp on bookmark - offering recap (legacy bookmark)');
      }
      
      recapCheckDoneRef.current = true; // Mark as done
      setShowRecapPrompt(true);
      return true;
    }
    
    console.log('[RECAP ELIGIBILITY] ✗ User at beginning of book, no recap needed');
    recapCheckDoneRef.current = true; // Mark as done even if no recap needed
    return false;
  };

  const generateRecap = async () => {
    const bookmark = loadedBookmarkDetails;
    if (!bookmark) {
      console.error('[RECAP] No bookmark details available');
      return;
    }
    
    // Note: recapCheckDoneRef already prevents modal from re-appearing
    // No need to set recapSkipped here - that's for when user explicitly skips
    
    console.log('[RECAP] ========== GENERATING RECAP ==========');
    console.log('[RECAP] loadedBookmarkDetails:', bookmark);
    console.log('[RECAP] Bookmark being sent to backend:');
    console.log('[RECAP]   - chapterIndex:', bookmark.chapterIndex);
    console.log('[RECAP]   - chunkIndex:', bookmark.chunkIndex);
    console.log('[RECAP]   - positionSeconds:', bookmark.positionSeconds);
    
    setRecapLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      // BACKEND_URL imported from centralized config
      
      const recapPayload = {
        manuscriptId: manuscript.id,
        bookmark: {
          chapterIndex: bookmark.chapterIndex,
          chunkIndex: bookmark.chunkIndex,
          positionSeconds: bookmark.positionSeconds
        },
        includeTTS: false  // Don't generate TTS automatically
      };
      
      console.log('[RECAP] Full payload being sent:', JSON.stringify(recapPayload, null, 2));
      
      const response = await axios.post(`${BACKEND_URL}/api/audia/recap`, recapPayload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('[RECAP] Received text response:', response.data);
      setRecapText(response.data.recapText);
      setRecapLoading(false);
      
      console.log('[RECAP] Text recap ready. User can click "Read Out Loud" for TTS.');
    } catch (err) {
      console.error('[RECAP] Failed to generate recap:', err);
      setRecapLoading(false);
      
      // ENHANCED: Handle recap failure gracefully
      // Show an error message and offer to skip to the book
      const errorMessage = err.response?.data?.detail || 'Unable to generate recap';
      console.log('[RECAP] Error detail:', errorMessage);
      
      // Set a fallback recap text explaining the issue
      setRecapText(`I'm sorry, I wasn't able to prepare a recap for you right now. ${errorMessage === 'Recap only available for Readers' ? 'This feature is only available for reader accounts.' : 'The book data may still be processing.'}\n\nYou can click "Resume" below to continue from where you left off.`);
    }
  };

  const generateRecapTTS = async () => {
    if (!recapText) {
      console.error('[RECAP TTS] No recap text available');
      return;
    }
    
    if (!manuscript?.id) {
      console.error('[RECAP TTS] No manuscript ID available');
      return;
    }
    
    console.log('[RECAP TTS] Starting TTS generation for recap...');
    setRecapLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      // BACKEND_URL imported from centralized config
      
      const response = await axios.post(`${BACKEND_URL}/api/audia/recap/tts`, {
        text: recapText,
        manuscriptId: manuscript.id
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('[RECAP TTS] Received TTS response');
      
      // Log AEU if generated
      if (response.data.aeuGenerated) {
        console.log(`[RECAP TTS] Generated ${response.data.aeuGenerated} AEU for recap TTS`);
      }
      
      if (response.data.audioBase64) {
        console.log('[RECAP TTS] Processing audio, base64 length:', response.data.audioBase64.length);
        const audioData = atob(response.data.audioBase64);
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecapAudioUrl(audioUrl);
        console.log('[RECAP TTS] Audio blob created, size:', audioBlob.size, 'bytes');
        
        // Set loading to false BEFORE playing audio
        setRecapLoading(false);
        
        // Auto-play recap
        const audio = new Audio(audioUrl);
        recapAudioRef.current = audio;
        
        audio.onended = () => {
          console.log('[RECAP TTS] ✓ Audio finished - closing modal and auto-resuming manuscript playback from bookmark');
          // toast.info('Continuing to your book...');
          // Close modal and resume playback with autoplay
          handleSkipRecap(true);
        };
        
        audio.onerror = (err) => {
          console.error('[RECAP TTS] Audio playback error:', err);
          // toast.error('Failed to play recap audio');
          // Still try to resume on error
          handleSkipRecap(true);
        };
        
        console.log('[RECAP TTS] Attempting autoplay...');
        await audio.play();
        console.log('[RECAP TTS] ✓ Autoplay started successfully - will auto-resume book when finished');
      } else {
        console.error('[RECAP TTS] No audio in response');
        setRecapLoading(false);
        // toast.error('Failed to generate audio');
      }
    } catch (err) {
      console.error('[RECAP] Failed to generate recap:', err);
      setRecapLoading(false);
      // Fall back to normal resume
      handleSkipRecap();
    }
  };

  const handleSkipRecap = async (autoplayAfter = true) => {
    console.log('[RECAP] Closing modal and resuming playback - autoplay:', autoplayAfter);
    
    // Mark recap as skipped to prevent re-showing
    setRecapSkipped(true);
    
    // Stop recap audio if playing
    if (recapAudioRef.current) {
      recapAudioRef.current.pause();
      recapAudioRef.current = null;
      console.log('[RECAP] Stopped recap audio');
    }
    
    // Close recap modal
    setShowRecapPrompt(false);
    setRecapText("");
    setRecapAudioUrl(null);
    
    // Resume normal playback from bookmark - this will jump to the bookmarked chapter and AUTOPLAY
    if (loadedBookmarkDetails) {
      console.log('[RECAP] Resuming manuscript playback from last bookmark position with AUTOPLAY');
      // toast.info('▶ Starting playback from where you left off...');
      
      // resumeFromBookmark() will:
      // 1. Jump to the bookmarked chapter
      // 2. Load the correct audio chunk
      // 3. Seek to the exact bookmark position
      // 4. Start playing automatically via playAudioNow()
      await resumeFromBookmark();
    } else {
      console.warn('[RECAP] No bookmark details available for resume');
      // toast.error('Could not resume - bookmark not found');
    }
  };

  const loadHardBookmark = async (manuscriptId, manuscriptData, outlineData) => {
    if (!manuscriptId) {
      console.log('[BOOKMARK] Cannot load - no manuscriptId provided');
      return;
    }
    
    // CRITICAL: Prevent duplicate loadHardBookmark calls during recap flow
    if (bookmarkLoadStartedRef.current) {
      console.log('[BOOKMARK] loadHardBookmark already in progress, skipping duplicate call');
      return;
    }
    bookmarkLoadStartedRef.current = true;

    // CRITICAL: Use captured initial state OR current location.state
    // The initial state is captured on mount to prevent it from being lost
    const navState = initialLocationStateRef.current || location.state || {};
    
    // Check navigation state
    const fromLibrary = navState.returnTo === '/reader/library' || navState.returnTo === '/reader/my-library';
    const shouldShowRecap = navState.showRecapPrompt === true;
    const startFromBeginning = navState.startFromBeginning === true;
    const passedBookmark = navState.bookmark; // Bookmark passed from library/modal
    
    console.log('[BOOKMARK LOAD] Navigation state:', {
      returnTo: navState.returnTo,
      fromLibrary,
      shouldShowRecap,
      showRecapPrompt: navState.showRecapPrompt,
      hasBookmark: !!passedBookmark,
      passedBookmark: passedBookmark,
      usedInitialState: !!initialLocationStateRef.current
    });
    
    // If user clicked "START OVER" from library, ignore bookmark and autoplay from beginning
    if (startFromBeginning) {
      console.log('[BOOKMARK] Start from beginning requested, jumping to chapter 0 and autoplaying');
      setTimeout(async () => {
        // Jump to chapter 0 first, then play
        await jumpToChapter(0);
      }, 500);
      return;
    }
    
    // Use the bookmark that was passed from the library page
    let bookmark = null;
    if (passedBookmark) {
      console.log('[BOOKMARK] Using bookmark passed from library:', passedBookmark);
      console.log('[BOOKMARK] positionSeconds from passed bookmark:', passedBookmark.positionSeconds);
      bookmark = {
        manuscriptId,
        chapterIndex: passedBookmark.chapterIndex ?? 0,
        chunkIndex: passedBookmark.chunkIndex ?? 0,
        positionSeconds: passedBookmark.positionSeconds ?? 0,
        type: 'passed'
      };
      console.log('[BOOKMARK] Constructed bookmark with positionSeconds:', bookmark.positionSeconds);
    }
    
    if (!bookmark) {
      if (isGuestSampleMode) {
        console.log('[BOOKMARK] Guest sample mode - waiting for bubble click to start playback');
        return;
      }
      console.log('[BOOKMARK] No bookmark passed - autoplaying from beginning');
      setTimeout(() => {
        executeCommand('play');
      }, 500);
      return;
    }

    try {
      console.log('[BOOKMARK] Loaded hard bookmark:', bookmark);
      
      // Store bookmark details
      setLoadedBookmarkDetails(bookmark);
      
      console.log('[BOOKMARK] Loaded bookmark:', {
        fromLibrary,
        shouldShowRecap,
        bookmarkChapter: bookmark.chapterIndex,
        bookmarkChunk: bookmark.chunkIndex,
        positionSeconds: bookmark.positionSeconds
      });
      
      // If coming from library with showRecapPrompt, check eligibility and show recap modal
      if (fromLibrary && shouldShowRecap) {
        console.log('[BOOKMARK] User clicked Resume - checking recap eligibility');
        const shouldOfferRecap = await checkRecapEligibility(bookmark);
        if (shouldOfferRecap) {
          console.log('[BOOKMARK] Showing recap modal - will NOT autoplay until user completes recap');
          // showRecapPrompt is set to true by checkRecapEligibility
          // Audio will start when user clicks "Continue" in recap modal
          return;
        }
      }
      
      // CRITICAL: If recap modal is currently showing, do NOT auto-resume
      // Wait for user to click "Resume" button in the recap modal
      if (showRecapPrompt) {
        console.log('[BOOKMARK] Recap modal is showing - NOT auto-resuming, waiting for user action');
        return;
      }
      
      // No recap needed - auto-resume immediately
      console.log('[BOOKMARK] No recap needed - auto-resuming with autoplay');
      // CRITICAL: Pass bookmark directly to avoid React state timing issues
      setTimeout(async () => {
        await resumeFromBookmark(bookmark);
      }, 500);
      
    } catch (err) {
      console.error('[BOOKMARK] Failed to parse bookmark:', err);
      setResumeTimeSeconds(null);
    }
  };

  // Resume playback from saved bookmark position
  // CRITICAL: Accept bookmark as parameter to avoid React state timing issues
  const resumeFromBookmark = async (bookmarkParam = null) => {
    // Use passed bookmark param if available, otherwise fall back to state
    const bookmark = bookmarkParam || loadedBookmarkDetails;
    
    console.log('[BOOKMARK RESUME] Starting resume...', {
      hasBookmarkParam: !!bookmarkParam,
      hasBookmarkState: !!loadedBookmarkDetails,
      usingBookmark: !!bookmark,
      hasManuscript: !!manuscript,
      hasOutline: !!outline,
      hasSession: !!session
    });
    
    if (!bookmark || !manuscript || !outline || !session) {
      console.error('[BOOKMARK RESUME] Missing required data:', {
        hasBookmark: !!bookmark,
        hasManuscript: !!manuscript,
        hasOutline: !!outline,
        hasSession: !!session
      });
      // toast.error('Cannot resume - missing bookmark data');
      return;
    }

    try {
      setCommandLoading(true);
      setGeneratingAudio(true);
      
      const targetChapterIndex = bookmark.chapterIndex ?? 0;
      const targetChapter = outline.chapters[targetChapterIndex];
      
      if (!targetChapter) {
        console.error(`[BOOKMARK RESUME] Chapter ${targetChapterIndex} not found in outline`);
        // toast.error('Bookmark chapter not found');
        setCommandLoading(false);
        setGeneratingAudio(false);
        return;
      }
      
      console.log('[BOOKMARK RESUME] Jumping to chapter:', targetChapterIndex, targetChapter);
      
      // Jump to the bookmarked chapter
      const jumpResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { 
          action: 'jump_to_chapter',
          chapter_index: targetChapterIndex
        }
      );
      
      const { session: updatedSession } = jumpResponse.data;
      setSession(updatedSession);
      
      // Now play from that chapter
      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );
      
      const { audio_url } = playResponse.data;
      
      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks } = chunksResponse.data;
        
        if (chunks && chunks.length > 0) {
          const targetChunkIndex = bookmark.chunkIndex ?? 0;
          const targetChunk = chunks[targetChunkIndex] || chunks[0];
          
          const audioSrc = `${BACKEND_URL}${targetChunk.audio_url}`;
          
          // Get bookmark time for resume
          const bookmarkTime = bookmark.positionSeconds || 0;
          console.log('[BOOKMARK RESUME] Bookmark details:', bookmark);
          console.log('[BOOKMARK RESUME] Position seconds from bookmark:', bookmark.positionSeconds);
          console.log('[BOOKMARK RESUME] Will seek to time:', bookmarkTime);
          
          if (bookmarkTime === 0) {
            console.error('[BOOKMARK RESUME] WARNING: Bookmark time is 0! Check if positionSeconds was saved correctly.');
          }
          
          // Calculate character position info
          const chunkStartChar = targetChunk.start_char || 0;
          const chunkEndChar = targetChunk.end_char || chunkStartChar + 1000;
          const chunkCharLength = chunkEndChar - chunkStartChar;
          
          const totalChars = manuscript.canonical_text?.length || 1;
          const initialProgress = (chunkStartChar / totalChars) * 100;
          
          // Only append title if it's different from label (avoid duplication)
          const chapterLabel = (targetChapter.title && targetChapter.title !== targetChapter.label)
            ? `${targetChapter.label} – ${targetChapter.title}`
            : targetChapter.label;
          
          // First, update the UI state to show correct chapter and position
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: audioSrc,
              chunks: chunks,
              chunkIndex: targetChunkIndex,
              chapterIndex: targetChapterIndex,
              chapterLabel: chapterLabel,
              charIndex: chunkStartChar,
              progress: initialProgress,
              shouldPlay: false,
            }
          });
          
          // Play the audio with bookmark time
          await playAudioNow(audioSrc, bookmarkTime);
          
          // After audio starts playing and seeks to position, update progress based on actual audio time
          // Wait longer to ensure seek has completed
          setTimeout(() => {
            if (audioRef.current && playerState.isPlaying) {
              const currentTime = audioRef.current.currentTime;
              const duration = audioRef.current.duration;
              
              if (currentTime > 0 && duration > 0 && !isNaN(duration)) {
                // Calculate character position based on actual current time
                const chunkProgress = currentTime / duration;
                const estimatedCharIndex = chunkStartChar + Math.floor(chunkCharLength * chunkProgress);
                const updatedProgress = (estimatedCharIndex / totalChars) * 100;
                
                dispatch({
                  type: 'UPDATE_PROGRESS',
                  payload: {
                    charIndex: estimatedCharIndex,
                    progress: updatedProgress
                  }
                });
                
                console.log('[BOOKMARK RESUME] Progress updated:', {
                  currentTime,
                  estimatedCharIndex,
                  progress: updatedProgress
                });
              }
            }
          }, 300); // Wait 300ms for seek to complete
          
          // toast.success(`Resumed: ${chapterLabel}`);
        }
      }
      
      setGeneratingAudio(false);
      setCommandLoading(false);
    } catch (error) {
      console.error('[BOOKMARK RESUME] Error:', error);
      console.error('[BOOKMARK RESUME] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // toast.error(`Failed to resume: ${error.response?.data?.detail || error.message}`);
      setGeneratingAudio(false);
      setCommandLoading(false);
    }
  };

  const loadSession = async () => {
    try {
      // Get auth token for authenticated requests
      const token = localStorage.getItem('accessToken');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      const sessionRes = await axios.get(`${API}/sessions/${sessionId}`, { headers: authHeaders });
      const sessionData = sessionRes.data;
      setSession(sessionData);
      
      // PREVIEW MODE: Update flag based on session data
      // Session may have is_preview flag or session_type indicating preview
      const sessionIsPreview = sessionData.is_preview === true || 
                               sessionData.session_type === 'simple_ingest_preview';
      if (sessionIsPreview) {
        setIsPreviewMode(true);
        console.log('[ManuscriptPlayer] Preview mode detected from session');
      }
      
      // SAMPLE PREVIEW MODE: Check session for sample_preview type
      const sessionIsSamplePreview = sessionData.is_sample_preview === true ||
                                     sessionData.session_type === 'sample_preview';
      if (sessionIsSamplePreview) {
        setIsSamplePreviewMode(true);
        console.log('[ManuscriptPlayer] Sample preview mode detected from session');
      }
      
      // PROMOTIONAL MODE: Check navigation state for promotional flag
      // Promotional books are full-length free titles, NOT sample-restricted
      const isPromotionalFromState = location.state?.isPromotional === true;
      if (isPromotionalFromState) {
        setIsPromotionalMode(true);
        console.log('[ManuscriptPlayer] Promotional mode detected from navigation state');
      }
      
      // GUEST SAMPLE MODE: Check session for guest_sample type
      // IMPORTANT: Skip if promotional mode - promotional books load full manuscript, not sample
      const sessionIsGuestSample = (sessionData.is_guest === true ||
                                   sessionData.session_type === 'guest_sample') && 
                                   !isPromotionalFromState;  // NOT promotional
      if (sessionIsGuestSample) {
        setIsGuestSampleMode(true);
        setIsSamplePreviewMode(true);  // Guest sample also uses sample preview mode for segment loading
        console.log('[ManuscriptPlayer] Guest sample mode detected from session');
        
        // Track sample play start for analytics (guest only)
        try {
          await axios.post(`${API}/manuscripts/${sessionData.manuscript_id}/sample/track`, {
            anonymous_id: sessionData.anonymous_id || anonymousId,
            session_id: sessionId,
            event_type: 'start',
            segments_played: [],
            listen_duration_seconds: 0
          });
          console.log('[ManuscriptPlayer] Guest sample play tracked');
        } catch (trackErr) {
          console.warn('[ManuscriptPlayer] Failed to track sample play:', trackErr);
        }
      }
      
      // Choose manuscript endpoint based on mode
      // - Preview mode uses /api/creator/manuscripts/{id} to bypass is_active checks
      // - Guest sample mode uses the public sample manifest endpoint
      // - Promotional mode uses public manuscript endpoint (full book)
      let manuscriptEndpoint;
      let manuscriptData;
      
      if (sessionIsGuestSample) {
        // Guest sample: fetch from public manifest endpoint (limited chapters)
        manuscriptEndpoint = `${API}/manuscripts/${sessionData.manuscript_id}/sample/manifest`;
        console.log(`[ManuscriptPlayer] Fetching guest sample manifest from: ${manuscriptEndpoint}`);
        const manifestRes = await axios.get(manuscriptEndpoint);
        const manifestData = manifestRes.data;
        
        // Convert manifest to manuscript-like object for player
        manuscriptData = {
          id: manifestData.manuscript_id,
          title: manifestData.title,
          author: manifestData.author,
          cover_image_path: manifestData.cover_image_path,
          sampleManifest: {
            sample_segments: manifestData.sample_segments
          },
          hasSample: true
        };
      } else if (isPromotionalFromState) {
        // Promotional mode: fetch full manuscript from public endpoint
        manuscriptEndpoint = `${API}/manuscripts/${sessionData.manuscript_id}`;
        console.log(`[ManuscriptPlayer] Fetching promotional manuscript from: ${manuscriptEndpoint}`);
        const manuscriptRes = await axios.get(manuscriptEndpoint);
        manuscriptData = manuscriptRes.data;
      } else {
        manuscriptEndpoint = (isPreviewMode || sessionIsPreview || sessionIsSamplePreview) 
          ? `${API}/creator/manuscripts/${sessionData.manuscript_id}`
          : `${API}/manuscripts/${sessionData.manuscript_id}`;
        
        console.log(`[ManuscriptPlayer] Fetching manuscript from: ${manuscriptEndpoint}`);
        const manuscriptRes = await axios.get(manuscriptEndpoint, { headers: authHeaders });
        manuscriptData = manuscriptRes.data;
      }
      
      setManuscript(manuscriptData);
      
      // Determine manuscript type
      const isV3 = manuscriptData.ingestion_version === 3;
      const isSimpleIngest = (
        manuscriptData.ingestion_method === "simple_ingest_v1" || 
        manuscriptData.simple_ingest_job_id != null
      );
      
      console.log('[ManuscriptPlayer] Manuscript type:', { isV3, isSimpleIngest, ingestion_version: manuscriptData.ingestion_version });
      
      // SAMPLE PREVIEW: If in sample preview mode (author or guest), load only sample segments
      if (isSamplePreviewMode || sessionIsSamplePreview || sessionIsGuestSample) {
        console.log('[ManuscriptPlayer] Loading sample segments for preview');
        
        // Load sample manifest from manuscript data
        const sampleManifest = manuscriptData.sampleManifest;
        if (sampleManifest?.sample_segments?.length > 0) {
          // Convert sample segments to player chapter format
          const sampleChapters = sampleManifest.sample_segments.map((seg, idx) => ({
            label: seg.title || `Sample ${idx + 1}`,
            title: seg.title || '',
            chapter_index: idx,
            segment_id: seg.segment_id,
            segment_index: seg.segment_index,
            audio_url: seg.audio_url,
            duration: seg.duration
          }));
          
          setOutline({ chapters: sampleChapters, is_sample: true });
          setChapterCount(sampleChapters.length);
          console.log('[ManuscriptPlayer] ✓ Loaded', sampleChapters.length, 'sample chapters for preview');
          
          // Set loading complete
          setLoading(false);
          return;
        } else {
          console.warn('[ManuscriptPlayer] No sample manifest found, falling back to full outline');
        }
      }
      
      // Load outline (single source of truth for chapters)
      let outlineData = null;
      let chapters = [];
      let manifestData = null;
      try {
        if (isV3) {
          // V3: Use v3 outline endpoint which returns segments
          // Use public endpoint if no auth token (guest session)
          const outlineEndpoint = token 
            ? `${API}/v3/manuscripts/${sessionData.manuscript_id}/outline`
            : `${API}/public/v3/manuscripts/${sessionData.manuscript_id}/outline`;
          const outlineRes = await axios.get(outlineEndpoint, { headers: authHeaders });
          outlineData = outlineRes.data;
          
          // P0 ARCHITECTURAL DIRECTIVE: Load audio manifest for v3 manuscripts
          // The manifest is the SINGLE SOURCE OF TRUTH for audio playback
          try {
            const manifestEndpoint = token
              ? `${API}/v3/manuscripts/${sessionData.manuscript_id}/audio-manifest`
              : `${API}/public/v3/manuscripts/${sessionData.manuscript_id}/audio-manifest`;
            const manifestRes = await axios.get(manifestEndpoint, { headers: authHeaders });
            manifestData = manifestRes.data;
            setAudioManifest(manifestData.manifest || []);
            console.log('[ManuscriptPlayer] ✓ Loaded audio manifest with', manifestData.manifest?.length, 'entries');
          } catch (manifestErr) {
            console.warn('[ManuscriptPlayer] Could not load audio manifest, will fall back to legacy path inference:', manifestErr.message);
          }
          
          // Map segments to chapters format for player compatibility
          // NOTE: segments from outline are ALREADY sorted by audio_order (playback sequence)
          chapters = (outlineData.segments || []).map((seg, idx) => ({
            label: seg.label,
            title: '',
            chapter_index: idx,
            segment_id: seg.id,
            segment_order: seg.order,  // Immutable order for file lookup
            audio_order: seg.audio_order  // Playback order
            // P0: include_in_tts REMOVED - all active segments have audio
          }));
          console.log('[ManuscriptPlayer] V3 manuscript loaded with', chapters.length, 'segments');
        } else if (isSimpleIngest) {
          // SIMPLE INGEST: Use standard outline endpoint (now returns segments correctly)
          // and load audio manifest from manuscript
          const outlineRes = await axios.get(`${API}/manuscripts/${sessionData.manuscript_id}/outline`, { headers: authHeaders });
          outlineData = outlineRes.data;
          
          // Map chapters to include segment_id for audio lookup
          chapters = (outlineData.chapters || []).map((ch, idx) => ({
            label: ch.label || ch.title || `Segment ${idx + 1}`,
            title: ch.title || '',
            chapter_index: idx,
            segment_id: ch.segment_id,
            word_count: ch.word_count || 0
          }));
          
          // Load audio manifest from manuscript if available
          if (manuscriptData.audio_manifest?.segments) {
            setAudioManifest(manuscriptData.audio_manifest.segments);
            console.log('[ManuscriptPlayer] ✓ Loaded simple ingest audio manifest with', manuscriptData.audio_manifest.segments.length, 'entries');
          }
          
          console.log('[ManuscriptPlayer] Simple ingest manuscript loaded with', chapters.length, 'chapters');
        } else {
          // V1/V2: Use original outline endpoint
          const outlineRes = await axios.get(`${API}/manuscripts/${sessionData.manuscript_id}/outline`, { headers: authHeaders });
          outlineData = outlineRes.data;
          chapters = outlineData.chapters || [];
        }
        
        setOutline({ ...outlineData, chapters });
        setChapterCount(chapters.length || 1);
        
        // Get chapter info from session
        const chapterIdx = sessionData.current_chapter_index ?? 0;
        const chapter = chapters[chapterIdx];
        let chapterLabel = 'Segment 1';
        
        if (chapter && chapter.title) {
          const cleanTitle = chapter.title.replace(/^[\-–—:\.\s]+/, '');
          chapterLabel = `${chapter.label} – ${cleanTitle}`;
        } else if (chapter) {
          chapterLabel = chapter.label;
        } else {
          chapterLabel = `Chapter ${chapterIdx + 1}`;
        }
        
        // Initialize player state with chapter info
        dispatch({
          type: 'SET_AUDIO_SOURCE',
          payload: {
            src: null,
            chunks: [],
            chunkIndex: 0,
            chapterIndex: chapterIdx,
            chapterLabel: chapterLabel,
            charIndex: sessionData.current_char_index || 0,
            progress: 0,
            shouldPlay: false,
          }
        });
      } catch (e) {
        console.error('Error loading outline:', e);
        setChapterCount(1);
      }
      
      if (sessionData.current_char_index > 0 && (manuscriptData.tts_generation_status === 'completed' || manuscriptData.audio_status === 'completed')) {
        // toast.info(`Resuming from position`);
      }
      
    } catch (error) {
      console.error('Error loading session:', error);
      playUrgentNotification();
      // toast.error('Failed to load session');
      navigate(returnTo);
    } finally {
      setLoading(false);
    }
  };
  
  // Load bookmark after component fully mounts and loading is complete
  useEffect(() => {
    // CRITICAL: Skip if recap modal is showing - don't interfere with recap flow
    if (showRecapPrompt) {
      console.log('[BOOKMARK] Recap modal showing - skipping bookmark load');
      return;
    }
    
    // CRITICAL: Skip if bookmark loading already started (prevents race conditions)
    if (bookmarkLoadStartedRef.current) {
      console.log('[BOOKMARK] Bookmark loading already started - skipping');
      return;
    }
    
    if (!loading && manuscript?.id && outline) {
      console.log('[BOOKMARK] Component loaded, checking for bookmark...');
      
      // Check if launched from Landing page FIRST
      const launchedFromLanding = localStorage.getItem('launchedFromLanding') === 'true';
      
      // Also check for autoplay flag from navigation state (promotional book)
      const shouldAutoplay = (launchedFromLanding || location.state?.autoplay === true) && !isGuestSampleMode;
      
      if (shouldAutoplay) {
        console.log('[BOOKMARK] Autoplay requested - starting from beginning, ignoring bookmark');
        // Clear the flag immediately if set
        if (launchedFromLanding) {
          localStorage.removeItem('launchedFromLanding');
        }
        
        // On mobile browsers, autoplay without user interaction is blocked.
        // We set a flag and attempt autoplay - if it fails, we show a "Tap to Play" prompt
        pendingAutoplayRef.current = true;
        
        // Trigger autoplay attempt after a short delay to ensure everything is loaded
        setTimeout(async () => {
          try {
            await executeCommand('play');
            // If successful, hide any mobile prompt
            setShowMobilePlayPrompt(false);
            pendingAutoplayRef.current = false;
          } catch (error) {
            console.log('[AUTOPLAY] Autoplay blocked by browser (likely mobile):', error);
            // Show mobile play prompt - user needs to tap to start
            setShowMobilePlayPrompt(true);
          }
        }, 500);
        return; // Don't load bookmark
      }
      
      // Load bookmark immediately once data is ready
      loadHardBookmark(manuscript.id, manuscript, outline);
    }
  }, [loading, manuscript?.id, outline]); // Removed showRecapPrompt - we use refs instead

  const executeCommand = async (action) => {
    if (!session || commandLoading) return;

    setCommandLoading(true);
    
    // P4.1: Start performance tracking for play actions
    if (action === 'play' || action === 'resume') {
      perfTracker.startPlayRequest(
        manuscript?.id,
        playerState.currentChapterIndex,
        playerState.currentChunkIndex
      );
      
      // Check if we already have audio loaded
      if (!playerState.audioSrc) {
        setGeneratingAudio(true);
        // Don't show "generating" toast - might already be generated
      }
    }

    // Get current audio playback time and position for pause/close
    let audioTime = null;
    let currentPosition = null;
    if ((action === 'pause' || action === 'close') && audioRef.current) {
      audioTime = audioRef.current.currentTime;
      // Send the actual character position (more accurate than audio_time conversion)
      currentPosition = playerState.realtimeCharIndex;
    }

    // Special case for 'close': Handle immediately without waiting for backend
    if (action === 'close') {
      // audioTime was already captured above at line ~1413 BEFORE we stop audio
      // This ensures we have the correct bookmark position
      
      // Capture session listen seconds BEFORE resetting
      const listenSecondsForClose = sessionListenSeconds;
      
      // OPTIMISTIC UI: Estimate credit debit and update immediately
      // Credit formula: PEU * 0.2 where PEU = listen_seconds / 60 (5 PEU = 1 RC)
      // PREVIEW MODE BYPASS: No credits charged in preview, sample preview, or guest sample mode
      const estimatedDebit = listenSecondsForClose > 0 ? (listenSecondsForClose / 60) * 0.2 : 0;
      let rollbackOptimistic = () => {};
      if (estimatedDebit > 0 && !isPreviewMode && !isSamplePreviewMode && !isGuestSampleMode) {
        rollbackOptimistic = optimisticDebit(estimatedDebit);
        console.log('[CLOSE] Optimistic credit debit:', estimatedDebit.toFixed(3));
      } else if (isPreviewMode || isSamplePreviewMode || isGuestSampleMode) {
        console.log('[CLOSE] Preview/Sample/Guest mode - no credit debit');
      }
      
      // Save bookmark FIRST (using audioTime captured above)
      // Skip bookmark for guest sample mode (no account to save to)
      if (!isGuestSampleMode) {
        await saveHardBookmark('close', audioTime);
      }
      
      // NOW STOP ALL AUDIO SOURCES
      
      // 1. Stop main book audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        console.log('[CLOSE] Stopped main book audio');
      }
      
      // 2. Stop recap TTS audio if playing
      if (recapAudioRef.current) {
        recapAudioRef.current.pause();
        recapAudioRef.current.src = '';
        recapAudioRef.current = null;
        console.log('[CLOSE] Stopped recap audio');
      }
      
      // 3. Close Audia chat modal (this triggers its own audio cleanup)
      if (isAudiaChatOpen) {
        setIsAudiaChatOpen(false);
        console.log('[CLOSE] Closed Audia chat modal');
      }
      
      // 4. Close recap modal
      if (showRecapPrompt) {
        setShowRecapPrompt(false);
        setRecapText("");
        setRecapAudioUrl(null);
        console.log('[CLOSE] Closed recap modal');
      }
      
      // Clean up audio element
      dispatch({ type: 'CLEAR_AUDIO' });
      stopProgressTracking();
      
      // Fire backend close command in background (don't wait)
      // NOTE: The backend close command now handles credit debits via peu_v2
      // Pass session_listen_seconds (actual elapsed playback time) NOT audio_time (file position)
      axios.post(
        `${API}/sessions/${session.id}/command`,
        { 
          action: 'close',
          audio_time: audioTime,  // For bookmark position only
          current_char_index: currentPosition,
          session_listen_seconds: listenSecondsForClose  // ACTUAL elapsed playback time for credit calc
        }
      ).then((response) => {
        // Sync with authoritative balance from backend (replaces optimistic estimate)
        if (response.data?.credits_balance !== undefined) {
          console.log('[CREDITS] Syncing with authoritative balance:', response.data.credits_balance);
          updateBalanceFromResponse(response.data.credits_balance);
        } else {
          // Fallback: refresh from summary endpoint
          console.log('[CREDITS] No balance in response, refreshing summary');
          refreshCreditsSummary(10);
        }
      }).catch(err => {
        console.warn('[CLOSE] Background session close failed:', err);
        // On error, rollback optimistic update and refresh
        rollbackOptimistic();
        refreshCreditsSummary(10);
      });
      
      // Reset local engagement counters (backend handles the actual credit debit)
      setSessionListenSeconds(0);
      lastTimeUpdateRef.current = null;
      
      console.log('[ManuscriptPlayer] Bookmark saved, navigating back to library');
      setCommandLoading(false);
      
      // Navigate directly back to library
      navigate(returnTo);
      return;
    } else {
      // For all other actions, wait for backend response
      try {
        const response = await axios.post(
          `${API}/sessions/${session.id}/command`,
          { 
            action,
            audio_time: audioTime,
            current_char_index: currentPosition
          }
        );

      const { session: updatedSession, audio_url, chapter_index } = response.data;
      setSession(updatedSession);

      // Calculate progress
      const newCharIndex = updatedSession.current_char_index || 0;
      const totalChars = manuscript.canonical_text?.length || 1;
      const newProgress = (newCharIndex / totalChars) * 100;
      
      // Get chapter label
      let chapterLabel = playerState.currentChapter;
      if (chapter_index !== undefined && chapter_index !== null && outline?.chapters) {
        const chapter = outline.chapters[chapter_index];
        if (chapter) {
          // Only append title if it's different from label (avoid duplication)
          const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
          chapterLabel = (chapter.title && cleanTitle !== chapter.label)
            ? `${chapter.label} – ${cleanTitle}` 
            : chapter.label;
        }
      }

      // Handle audio playback with chunk-based system
      if (action === 'play' || action === 'resume') {
        // P4.3: Quick resume - if audio is already loaded and just paused, resume directly
        if (audioRef.current && playerState.audioSrc && !playerState.isPlaying) {
          const existingDuration = audioRef.current.duration;
          const existingTime = audioRef.current.currentTime;
          
          // If we have a valid audio buffer, just resume
          if (existingDuration > 0 && existingTime > 0 && existingTime < existingDuration - 0.5) {
            console.log('[P4.3 QUICK RESUME] Audio already loaded, resuming directly');
            perfTracker.markAudioRequestStart();
            perfTracker.markCached(); // Mark as cached since we're using buffered audio
            
            try {
              await audioRef.current.play();
              dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
              startProgressTracking();
              setCommandLoading(false);
              setGeneratingAudio(false);
              console.log('[P4.3 QUICK RESUME] ✓ Resumed from buffer without refetch');
              return;
            } catch (playError) {
              console.warn('[P4.3 QUICK RESUME] Failed to resume, falling back to normal flow:', playError);
              // Fall through to normal flow
            }
          }
        }
        
        // SAMPLE PREVIEW MODE: Use audio URLs directly from sample manifest
        // The sample manifest already contains correct audio_url paths pointing to R2 files
        // Skip the backend chunks endpoint to avoid index misalignment
        if ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample && outline?.chapters) {
          const currentChapterIdx = chapter_index ?? playerState.currentChapterIndex ?? 0;
          const sampleChapter = outline.chapters[currentChapterIdx];
          
          if (sampleChapter?.audio_url) {
            console.log('[SAMPLE PLAY] Using direct audio URL from sample manifest');
            console.log('[SAMPLE PLAY] Chapter index:', currentChapterIdx);
            console.log('[SAMPLE PLAY] Chapter title:', sampleChapter.label || sampleChapter.title);
            console.log('[SAMPLE PLAY] Original segment_index:', sampleChapter.segment_index);
            console.log('[SAMPLE PLAY] Audio URL:', sampleChapter.audio_url);
            
            // audio_url from manifest is either:
            // - /api/simple-ingest/jobs/{job_id}/audio/{segment_id} (new format)
            // - manuscripts/{id}/audio/segments/seg-XXXX.mp3 (legacy R2 path)
            const audioPath = sampleChapter.audio_url.startsWith('/') 
              ? sampleChapter.audio_url 
              : `/${sampleChapter.audio_url}`;
            const audioSrc = `${BACKEND_URL}${audioPath}`;
            console.log('[SAMPLE PLAY] Full audio src:', audioSrc);
            
            // Build a synthetic chunk for compatibility with player state
            const syntheticChunk = {
              audio_url: audioPath,
              start_char: 0,
              end_char: 0,
              duration: sampleChapter.duration || 0
            };
            
            dispatch({
              type: 'SET_AUDIO_SOURCE',
              payload: {
                src: audioSrc,
                chunks: [syntheticChunk],
                chunkIndex: 0,
                chapterIndex: currentChapterIdx,
                chapterLabel: sampleChapter.label || sampleChapter.title || `Sample ${currentChapterIdx + 1}`,
                charIndex: 0,
                progress: 0,
                shouldPlay: false,
              }
            });
            
            await playAudioNow(audioSrc);
            setGeneratingAudio(false);
            setCommandLoading(false);
            return;
          } else {
            console.error('[SAMPLE PLAY] No audio_url in sample chapter:', sampleChapter);
          }
        }
        
        // NORMAL PLAYBACK: Use backend chunks endpoint
        if (audio_url) {
          try {
            const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
            const { chunks } = chunksResponse.data;
            
            if (chunks && chunks.length > 0) {
              const audioSrc = `${BACKEND_URL}${chunks[0].audio_url}`;
              
              // Dispatch state update
              dispatch({
                type: 'SET_AUDIO_SOURCE',
                payload: {
                  src: audioSrc,
                  chunks: chunks,
                  chunkIndex: 0,
                  chapterIndex: chapter_index ?? playerState.currentChapterIndex,
                  chapterLabel: chapterLabel,
                  charIndex: chunks[0].start_char,
                  progress: newProgress,
                  shouldPlay: false,
                }
              });
              
              // Play audio directly
              await playAudioNow(audioSrc);
              // toast.success('Audio ready!');
            } else {
              // toast.error('No audio chunks available');
            }
          } catch (error) {
            console.error('Error fetching audio chunks:', error);
            playNotificationSound('error');
            // toast.error('Failed to load audio. Please try again.');
          }
        }
        setGeneratingAudio(false);
      } else if (action === 'pause') {
        // Save bookmark on pause (for resume without modal) - use already captured audioTime
        await saveHardBookmark('pause', audioTime);
        
        if (audioRef.current) {
          audioRef.current.pause();
          dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
          stopProgressTracking();
        }
        // Flush engagement on pause
        await flushEngagement();
      }
      // Note: Close handling moved to top of function for immediate response
      
      // Handle chapter navigation commands
      if (action === 'prev_chapter' || action === 'next_chapter') {
        const wasPlaying = playerState.isPlaying;
        if (audioRef.current) {
          audioRef.current.pause();
          dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
        }
        
        if (action === 'prev_chapter') {
          // toast.info('Jumped to previous chapter');
        } else if (action === 'next_chapter') {
          // toast.info('Jumped to next chapter');
        }
        
        setTimeout(async () => {
          try {
            setGeneratingAudio(true);
            
            const playResponse = await axios.post(
              `${API}/sessions/${session.id}/command`,
              { action: 'play' }
            );
            
            const { session: playSession, audio_url, chapter_index: playChapterIndex } = playResponse.data;
            setSession(playSession);
            
            let chapterLabel = 'Segment 1';
            if (playChapterIndex !== undefined && playChapterIndex !== null && outline?.chapters) {
              const chapter = outline.chapters[playChapterIndex];
              if (chapter) {
                // Only append title if it's different from label (avoid duplication)
                const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
                chapterLabel = (chapter.title && cleanTitle !== chapter.label)
                  ? `${chapter.label} – ${cleanTitle}` 
                  : chapter.label;
              }
            }
            
            if (audio_url) {
              const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
              const { chunks } = chunksResponse.data;
              
              if (chunks && chunks.length > 0) {
                const newCharIndex = chunks[0].start_char;
                const totalChars = manuscript.canonical_text?.length || 1;
                const newProgress = (newCharIndex / totalChars) * 100;
                
                dispatch({
                  type: 'SET_AUDIO_SOURCE',
                  payload: {
                    src: `${BACKEND_URL}${chunks[0].audio_url}`,
                    chunks: chunks,
                    chunkIndex: 0,
                    chapterIndex: playChapterIndex ?? playerState.currentChapterIndex,
                    chapterLabel: chapterLabel,
                    charIndex: newCharIndex,
                    progress: newProgress,
                    shouldPlay: wasPlaying,
                  }
                });
                
                if (wasPlaying) {
                  startProgressTracking();
                  // toast.success('Playing from new chapter');
                } else {
                  // toast.success('Chapter loaded - press Play to start');
                }
              }
            }
            setGeneratingAudio(false);
          } catch (error) {
            console.error('Error loading new chapter:', error);
            // toast.error('Failed to load chapter audio');
            setGeneratingAudio(false);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Command error:', error);
      
      // Special handling for CLOSE command - navigate away even if it fails
      if (action === 'close') {
        // IMPORTANT: Save bookmark BEFORE cleaning up audio
        saveHardBookmark('close');
        
        // Clean up audio element
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        }
        dispatch({ type: 'CLEAR_AUDIO' });
        stopProgressTracking();
        
        // Flush engagement on close
        await flushEngagement();
        
        // Navigate away (session might be deleted, that's okay)
        navigate(returnTo);
        return; // Exit early, don't show error toast
      }
      
      // For other commands, show error
      const errorMsg = error.response?.data?.detail || 'Command failed';
      playNotificationSound('error');
      // toast.error(errorMsg);
      setGeneratingAudio(false);
    } finally {
      setCommandLoading(false);
    }
    } // Close else block for (action !== 'close')
  };

  // Compute words from listening seconds (2.5 words/second for TTS)
  const computeWordsFromSeconds = (seconds) => {
    const WORDS_PER_SECOND = 2.5;
    return Math.round(seconds * WORDS_PER_SECOND);
  };

  // Flush engagement data to backend
  const flushEngagement = async () => {
    if (!manuscript || sessionListenSeconds <= 0) return;

    // Check if we're in author preview mode by looking at returnTo state and role
    // Handle both canonical /author-console and legacy /creator-console paths
    const isAuthorPreview = location.state?.returnTo === '/author-console' || 
                            location.state?.returnTo === '/creator-console';
    const role = location.state?.role || 'reader';
    
    // Skip EU generation for author previews, sample previews, and guest samples
    // Authors don't get charged for previewing their own books or samples
    // Guests have no account to track engagement against
    if (isAuthorPreview || isSamplePreviewMode || isGuestSampleMode) {
      console.log('[ENGAGEMENT] Skipping EU generation for author/sample/guest preview');
      // Reset local counters
      setSessionListenSeconds(0);
      lastTimeUpdateRef.current = null;
      return;
    }

    const listenSeconds = sessionListenSeconds;
    const wordsConsumed = computeWordsFromSeconds(listenSeconds);

    // Reset local counters optimistically
    setSessionListenSeconds(0);
    lastTimeUpdateRef.current = null;

    // Get authenticated user ID from session
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.log('[ENGAGEMENT] No auth token - skipping engagement tracking');
      return;
    }

    // Get current playback position for bookmark
    const currentTime = audioRef.current?.currentTime || 0;
    const last_position = {
      current_chapter_index: playerState.currentChapterIndex,
      current_chunk_index: playerState.currentChunkIndex,
      position_seconds: currentTime
    };

    try {
      // Generate client_event_id for retry-safe idempotency (MANDATORY for credit debits)
      const clientEventId = uuidv4();
      
      const res = await axios.post(
        `${API}/manuscripts/${manuscript.id}/engagement`,
        {
          listen_seconds: listenSeconds,
          words_consumed: wordsConsumed,
          role: role,
          last_position: last_position,
          client_event_id: clientEventId  // MANDATORY: Enables credit debit recording
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log('[ENGAGEMENT] Logged:', res.data);
      console.log('[BOOKMARK] Saved to database:', last_position);
      
      // Log credit debit result if present
      if (res.data.debit_result) {
        console.log('[CREDITS] Debit result:', res.data.debit_result, 'Balance:', res.data.balance);
      }
      
      // Update UI credit balance from authoritative backend response
      if (typeof res.data.balance === 'number') {
        updateBalanceFromResponse(res.data.balance);
      }
    } catch (err) {
      console.error('[ENGAGEMENT] Error logging engagement:', err);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || !manuscript) return;
    
    const audio = audioRef.current;
    const currentTime = audio.currentTime;
    const duration = audio.duration;
    
    // Store the last valid currentTime for bookmark capture
    if (currentTime > 0 && !isNaN(currentTime)) {
      lastValidTimeRef.current = currentTime;
    }
    
    // Track engagement: listening time
    if (playerState.isPlaying && currentTime > 0) {
      if (lastTimeUpdateRef.current === null) {
        lastTimeUpdateRef.current = currentTime;
      } else {
        const delta = currentTime - lastTimeUpdateRef.current;
        if (delta > 0 && delta < 2) { // Sanity check: delta should be small
          setSessionListenSeconds(prev => prev + delta);
        }
        lastTimeUpdateRef.current = currentTime;
      }
    }
    
    // Only update if we have valid time values
    if (currentTime > 0 && duration > 0 && !isNaN(duration)) {
      const currentChunk = playerState.audioChunks[playerState.currentChunkIndex];
      
      if (currentChunk) {
        // Calculate character position based on audio progress within current chunk
        const chunkProgress = currentTime / duration; // 0 to 1
        const chunkStartChar = currentChunk.start_char || 0;
        const chunkEndChar = currentChunk.end_char || chunkStartChar + 1000;
        const chunkCharLength = chunkEndChar - chunkStartChar;
        
        const estimatedCharIndex = chunkStartChar + Math.floor(chunkCharLength * chunkProgress);
        
        const totalChars = manuscript.canonical_text?.length || 1;
        const newProgress = (estimatedCharIndex / totalChars) * 100;
        
        dispatch({
          type: 'UPDATE_PROGRESS',
          payload: {
            charIndex: estimatedCharIndex,
            progress: newProgress
          }
        });
      }
    }
  };

  // Helper function to advance to next chapter (used by both Next button and auto-advance)
  const goToNextChapter = async () => {
    if (!session || !outline || !manuscript) return;
    
    const currentIdx = playerState.currentChapterIndex ?? 0;
    const maxIdx = (outline.chapters?.length || 1) - 1;
    
    // SAMPLE MODE: Use jumpToChapter which has sample-aware logic
    // Navigate only within outline.chapters (which contains only sample segments in sample mode)
    if ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample) {
      if (currentIdx >= maxIdx) {
        console.log('[SAMPLE NEXT] Already at last sample chapter, cannot advance');
        return; // Already at last sample chapter
      }
      const nextIdx = currentIdx + 1;
      console.log(`[SAMPLE NEXT] Jumping to sample chapter ${nextIdx + 1}/${outline.chapters.length}`);
      await jumpToChapter(nextIdx);
      return;
    }
    
    // NORMAL MODE: Use backend next_chapter command
    const wasPlaying = playerState.isPlaying;
    if (audioRef.current) {
      audioRef.current.pause();
      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
    }
    
    try {
      setGeneratingAudio(true);
      
      // Send next_chapter command to backend
      await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'next_chapter' }
      );
      
      // Get updated audio from backend
      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );
      
      const { session: playSession, audio_url, chapter_index: playChapterIndex } = playResponse.data;
      setSession(playSession);
      
      let chapterLabel = 'Segment 1';
      if (playChapterIndex !== undefined && playChapterIndex !== null && outline?.chapters) {
        const chapter = outline.chapters[playChapterIndex];
        if (chapter) {
          // Only append title if it's different from label (avoid duplication)
          const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
          chapterLabel = (chapter.title && cleanTitle !== chapter.label)
            ? `${chapter.label} – ${cleanTitle}` 
            : chapter.label;
        }
      }
      
      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks } = chunksResponse.data;
        
        if (chunks && chunks.length > 0) {
          const newCharIndex = chunks[0].start_char;
          const totalChars = manuscript.canonical_text?.length || 1;
          const newProgress = (newCharIndex / totalChars) * 100;
          const audioSrc = `${BACKEND_URL}${chunks[0].audio_url}`;
          
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: audioSrc,
              chunks: chunks,
              chunkIndex: 0,
              chapterIndex: playChapterIndex ?? playerState.currentChapterIndex,
              chapterLabel: chapterLabel,
              charIndex: newCharIndex,
              progress: newProgress,
              shouldPlay: false,
            }
          });
          
          if (wasPlaying) {
            await playAudioNow(audioSrc);
          }
        }
      }
      setGeneratingAudio(false);
    } catch (error) {
      console.error('[NEXT CHAPTER] Error:', error);
      // toast.error('Failed to load next chapter');
      setGeneratingAudio(false);
    }
  };

  // Helper function to go to previous chapter (used by Previous button)
  const goToPrevChapter = async () => {
    if (!session || !outline || !manuscript) return;
    
    const currentIdx = playerState.currentChapterIndex ?? 0;
    
    // SAMPLE MODE: Use jumpToChapter which has sample-aware logic
    // Navigate only within outline.chapters (which contains only sample segments in sample mode)
    if ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample) {
      if (currentIdx <= 0) {
        console.log('[SAMPLE PREV] Already at first sample chapter, cannot go back');
        return; // Already at first sample chapter
      }
      const prevIdx = currentIdx - 1;
      console.log(`[SAMPLE PREV] Jumping to sample chapter ${prevIdx + 1}/${outline.chapters.length}`);
      await jumpToChapter(prevIdx);
      return;
    }
    
    // NORMAL MODE: Use backend prev_chapter command
    const wasPlaying = playerState.isPlaying;
    if (audioRef.current) {
      audioRef.current.pause();
      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
    }
    
    try {
      setGeneratingAudio(true);
      
      // Send prev_chapter command to backend
      await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'prev_chapter' }
      );
      
      // Get updated audio from backend
      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );
      
      const { session: playSession, audio_url, chapter_index: playChapterIndex } = playResponse.data;
      setSession(playSession);
      
      let chapterLabel = 'Segment 1';
      if (playChapterIndex !== undefined && playChapterIndex !== null && outline?.chapters) {
        const chapter = outline.chapters[playChapterIndex];
        if (chapter) {
          // Only append title if it's different from label (avoid duplication)
          const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
          chapterLabel = (chapter.title && cleanTitle !== chapter.label)
            ? `${chapter.label} – ${cleanTitle}` 
            : chapter.label;
        }
      }
      
      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks } = chunksResponse.data;
        
        if (chunks && chunks.length > 0) {
          const newCharIndex = chunks[0].start_char;
          const totalChars = manuscript.canonical_text?.length || 1;
          const newProgress = (newCharIndex / totalChars) * 100;
          const audioSrc = `${BACKEND_URL}${chunks[0].audio_url}`;
          
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: audioSrc,
              chunks: chunks,
              chunkIndex: 0,
              chapterIndex: playChapterIndex ?? playerState.currentChapterIndex,
              chapterLabel: chapterLabel,
              charIndex: newCharIndex,
              progress: newProgress,
              shouldPlay: false,
            }
          });
          
          if (wasPlaying) {
            await playAudioNow(audioSrc);
          }
        }
      }
      setGeneratingAudio(false);
    } catch (error) {
      console.error('[PREV CHAPTER] Error:', error);
      // toast.error('Failed to load previous chapter');
      setGeneratingAudio(false);
    }
  };

  const handleAudioEnded = async () => {
    console.log(`[AUDIO ENDED] Chunk ${playerState.currentChunkIndex + 1}/${playerState.audioChunks.length}`);
    
    // Auto-advance to next chunk for seamless playback
    if (playerState.currentChunkIndex < playerState.audioChunks.length - 1) {
      const nextIndex = playerState.currentChunkIndex + 1;
      const nextChunk = playerState.audioChunks[nextIndex];
      
      console.log(`[CHUNK ADVANCE] Advancing to chunk ${nextIndex + 1}/${playerState.audioChunks.length}`);
      
      // P4.1: Start performance tracking for chunk transition
      perfTracker.startPlayRequest(
        manuscript?.id,
        playerState.currentChapterIndex,
        nextIndex
      );
      
      // Find which chapter this chunk belongs to
      let newChapterIndex = playerState.currentChapterIndex;
      let newChapterLabel = playerState.currentChapter;
      
      if (outline && outline.chapters && manuscript) {
        const chunkStartChar = nextChunk.start_char || 0;
        
        // Find the chapter that contains this chunk's start position
        for (let i = 0; i < outline.chapters.length; i++) {
          const chapter = outline.chapters[i];
          const chapterStart = chapter.start_char_index || 0;
          const chapterEnd = i < outline.chapters.length - 1 
            ? (outline.chapters[i + 1].start_char_index || Infinity)
            : Infinity;
          
          if (chunkStartChar >= chapterStart && chunkStartChar < chapterEnd) {
            newChapterIndex = i;
            // Only append title if it's different from label (avoid duplication)
            const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
            newChapterLabel = (chapter.title && cleanTitle !== chapter.label)
              ? `${chapter.label} – ${cleanTitle}`
              : chapter.label;
            break;
          }
        }
      }
      
      // P4.2: Check prefetch cache first
      const cachedBlobUrl = audioCache.get(manuscript?.id, newChapterIndex, nextIndex);
      const nextAudioSrc = cachedBlobUrl || `${BACKEND_URL}${nextChunk.audio_url}`;
      const fromCache = !!cachedBlobUrl;
      
      console.log(`[CHUNK ADVANCE] Dispatching SET_AUDIO_SOURCE with chapterIndex: ${newChapterIndex}, chunkIndex: ${nextIndex}, fromCache: ${fromCache}`);
      
      dispatch({
        type: 'SET_AUDIO_SOURCE',
        payload: {
          src: nextAudioSrc,
          chunks: playerState.audioChunks,
          chunkIndex: nextIndex,
          chapterIndex: newChapterIndex,
          chapterLabel: newChapterLabel,
          charIndex: nextChunk.start_char || 0,
          progress: playerState.progressPercent,
          shouldPlay: false,
        }
      });
      
      console.log(`[CHUNK ADVANCE] State updated, playerState.currentChapterIndex should now be: ${newChapterIndex}`);
      
      // Play next chunk directly (P4.2: with cache info for metrics)
      await playAudioNow(nextAudioSrc, null, { 
        fromCache, 
        manuscriptId: manuscript?.id,
        chapterIndex: newChapterIndex,
        chunkIndex: nextIndex
      });
      console.log(`[CHUNK ADVANCE] Now playing chunk ${nextIndex + 1}, chapter: ${newChapterLabel}`);
    } else {
      // No more chunks in current chapter
      // Check if this is the final chapter
      const isLastChapter = outline && playerState.currentChapterIndex >= (outline.chapters?.length - 1);
      
      if (isLastChapter) {
        // End of book reached - show completion modal
        console.log('[BOOK COMPLETE] Reached end of audiobook');
        if (audioRef.current) {
          audioRef.current.pause();
        }
        dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
        // Flush engagement when book completes
        await flushEngagement();
        
        // GUEST SAMPLE: Show CTA overlay for unauthenticated users
        if (isGuestSampleMode) {
          console.log('[GUEST SAMPLE] Sample complete - showing signup CTA');
          
          // Track completion event
          try {
            await axios.post(`${API}/manuscripts/${manuscript?.id}/sample/track`, {
              anonymous_id: anonymousId,
              session_id: sessionId,
              event_type: 'complete',
              segments_played: outline?.chapters?.map(c => c.segment_id) || [],
              listen_duration_seconds: sessionListenSeconds
            });
          } catch (trackErr) {
            console.warn('[GUEST SAMPLE] Failed to track completion:', trackErr);
          }
          
          playNotificationSound('success');
          
          // RADIO MODE: Show "next track" modal instead of registration CTA
          if (isRadioMode) {
            console.log('[RADIO] Sample complete - showing next track modal');
            setShowRadioNextModal(true);
            return;
          }
          
          setShowGuestSampleCTA(true);
          return;
        }
        
        // AUTHOR SAMPLE PREVIEW: Don't show completion modal, just return to title page
        if (isSamplePreviewMode) {
          console.log('[SAMPLE PREVIEW] Sample complete - returning to title page');
          playNotificationSound('success');
          navigate(returnTo || '/author');
          return;
        }
        
        setShowCompletionModal(true);
        playNotificationSound('success');
      } else {
        // Not the last chapter - advance to next chapter
        console.log('[AUTO-ADVANCE] Reached end of chapter, advancing to next chapter');
        goToNextChapter();
      }
    }
  };

  // P6: Track audio error retry count to prevent infinite loops
  const audioErrorRetryCountRef = useRef(0);
  const MAX_AUDIO_RETRIES = 3;
  
  const handleAudioError = (e) => {
    console.error('Audio error:', e);
    console.error('[AUDIO ERROR] Details:', {
      error: e?.target?.error,
      src: audioRef.current?.src,
      readyState: audioRef.current?.readyState,
      networkState: audioRef.current?.networkState,
      retryCount: audioErrorRetryCountRef.current
    });
    
    // P6: Check if we've exceeded max retries to prevent infinite loop
    if (audioErrorRetryCountRef.current >= MAX_AUDIO_RETRIES) {
      console.error('[AUDIO ERROR] Max retries exceeded, stopping playback');
      playNotificationSound('error');
      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
      stopProgressTracking();
      playbackInProgressRef.current = false;
      
      // Reset retry count for next playback attempt
      audioErrorRetryCountRef.current = 0;
      return;
    }
    
    // P6: Validate audio source before retrying
    const currentSrc = audioRef.current?.src;
    if (!currentSrc || currentSrc === '' || currentSrc === window.location.href) {
      console.error('[AUDIO ERROR] Invalid audio source, not retrying:', currentSrc);
      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
      stopProgressTracking();
      playbackInProgressRef.current = false;
      audioErrorRetryCountRef.current = 0;
      return;
    }
    
    playNotificationSound('warning');
    audioErrorRetryCountRef.current++;
    console.log(`[AUDIO ERROR] Retry attempt ${audioErrorRetryCountRef.current}/${MAX_AUDIO_RETRIES}`);
    
    setTimeout(() => {
      if (audioRef.current && playerState.audioChunks.length > playerState.currentChunkIndex) {
        audioRef.current.load();
        audioRef.current.play().catch(err => {
          console.error('Retry failed:', err);
          // Don't call handleAudioError recursively - onError event will handle it
        });
      }
    }, 1000);
  };
  
  // P6: Reset retry count on successful playback
  const resetAudioErrorRetries = () => {
    audioErrorRetryCountRef.current = 0;
  };

  const startProgressTracking = () => {
    // Progress tracking now handled by onTimeUpdate event
    // This function kept for compatibility but no longer uses intervals
    console.log('Progress tracking enabled via onTimeUpdate event');
  };

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  // Cleanup interval on unmount and flush engagement
  useEffect(() => {
    return () => {
      stopProgressTracking();
      // Save bookmark on unmount
      if (manuscript?.id) {
        saveHardBookmark('close');
        console.log('[BOOKMARK] Saved on component unmount');
      }
      flushEngagement();
    };
  }, [manuscript?.id]);
  
  // Save bookmark on window/tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (manuscript?.id) {
        saveHardBookmark('close');
        console.log('[BOOKMARK] Saved on window beforeunload');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [manuscript?.id, playerState.currentChapterIndex, playerState.currentChunkIndex]);

  const getProgress = () => {
    if (!manuscript) return 0;
    const charIndex = playerState.isPlaying ? playerState.realtimeCharIndex : (session?.current_char_index || 0);
    const totalChars = manuscript.canonical_text?.length || 1;
    return (charIndex / totalChars) * 100;
  };
  
  const getCharPosition = () => {
    if (!manuscript) return { current: 0, total: 0 };
    const current = playerState.isPlaying ? playerState.realtimeCharIndex : (session?.current_char_index || 0);
    return {
      current: current,
      total: manuscript.canonical_text?.length || 0
    };
  };

  // Helper: Calculate chapter index from character position (used ONLY for initial load)
  const getChapterIndexFromCharIndex = (charIndex, chapters) => {
    if (!chapters || chapters.length === 0) return 0;

    for (let i = 0; i < chapters.length; i++) {
      const start = chapters[i].start_char_index || 0;
      const nextStart = i < chapters.length - 1 
        ? (chapters[i + 1].start_char_index || Infinity)
        : Infinity;

      if (charIndex >= start && charIndex < nextStart) {
        return i; // 0-based index
      }
    }

    return chapters.length - 1; // Default to last chapter if beyond all bounds
  };

  const getChapterInfo = () => {
    if (!outline || !outline.chapters) return { current: 1, total: 1, label: 'Segment 1' };
    
    const currentChap = outline.chapters[playerState.currentChapterIndex];
    
    return {
      current: playerState.currentChapterIndex + 1,
      total: outline.chapters.length,
      label: currentChap?.label || `Segment ${playerState.currentChapterIndex + 1}`
    };
  };

  // Helper to get chapter count
  const getChapterCount = () => {
    if (outline && outline.chapters) return outline.chapters.length;
    return 0;
  };

  // Time-based rewind/ff (frontend-only) with cross-chapter support
  const handleRewind = async () => {
    const audio = audioRef.current;
    if (!audio || !manuscript) return;

    const currentTime = audio.currentTime || 0;

    // Case 1: Simple rewind within current chapter (more than 10 seconds available)
    if (currentTime > 10) {
      audio.currentTime = currentTime - 10;
      // toast.info('Rewound 10 seconds');
      return;
    }

    // Case 2: At or near start of chapter - need to check if we can go to previous chapter
    if (playerState.currentChapterIndex === 0) {
      // Already at first chapter - clamp to absolute start
      audio.currentTime = 0;
      // toast.info('At beginning of audiobook');
      return;
    }

    // Case 3: Cross chapter boundary - move to previous chapter
    const remaining = 10 - currentTime;
    const targetChapterIndex = playerState.currentChapterIndex - 1;

    try {
      setCommandLoading(true);
      
      // Stop current playback
      const wasPlaying = playerState.isPlaying;
      if (audio) {
        audio.pause();
        dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
        stopProgressTracking();
      }

      // Jump to previous chapter
      const jumpResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { 
          action: 'jump_to_chapter',
          chapter_index: targetChapterIndex
        }
      );

      const { session: updatedSession, chapter_index: returnedChapterIndex } = jumpResponse.data;
      setSession(updatedSession);

      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );

      const { audio_url } = playResponse.data;

      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks } = chunksResponse.data;

        if (chunks && chunks.length > 0) {
          const lastChunkIndex = chunks.length - 1;
          const lastChunk = chunks[lastChunkIndex];
          
          const chapter = outline?.chapters?.[returnedChapterIndex ?? targetChapterIndex];
          let chapterLabel = 'Chapter ' + (targetChapterIndex + 1);
          if (chapter) {
            // Only append title if it's different from label (avoid duplication)
            const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
            chapterLabel = (chapter.title && cleanTitle !== chapter.label)
              ? `${chapter.label} – ${cleanTitle}` 
              : chapter.label;
          }
          
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: null, // Will be set after metadata loads
              chunks: chunks,
              chunkIndex: lastChunkIndex,
              chapterIndex: returnedChapterIndex ?? targetChapterIndex,
              chapterLabel: chapterLabel,
              charIndex: lastChunk.start_char,
              progress: 0,
              shouldPlay: false,
            }
          });

          audio.src = `${BACKEND_URL}${lastChunk.audio_url}`;

          const onLoadedMetadata = () => {
            const duration = audio.duration;
            if (!Number.isNaN(duration)) {
              const targetTime = Math.max(0, duration - remaining);
              audio.currentTime = targetTime;
              
              if (wasPlaying) {
                audio.play().then(() => {
                  dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                  startProgressTracking();
                });
              }
            }
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
          };

          audio.addEventListener('loadedmetadata', onLoadedMetadata);
          // toast.info(`Rewound to previous chapter`);
        }
      }
    } catch (error) {
      console.error('Cross-chapter rewind failed:', error);
      // toast.error('Failed to rewind to previous chapter');
    } finally {
      setCommandLoading(false);
    }
  };

  const handleFastForward = async () => {
    const audio = audioRef.current;
    if (!audio || Number.isNaN(audio.duration) || !manuscript) return;

    const currentTime = audio.currentTime || 0;
    const duration = audio.duration;
    const timeRemaining = duration - currentTime;

    // Case 1: Simple fast-forward within current chapter (more than 10 seconds available)
    if (timeRemaining > 10) {
      audio.currentTime = currentTime + 10;
      // toast.info('Fast-forwarded 10 seconds');
      return;
    }

    // Case 2: At or near end of chapter - check if we can go to next chapter
    try {
      if (!outline || !outline.chapters) {
        audio.currentTime = duration;
        // toast.info('At end of audiobook');
        return;
      }
      
      const chapters = outline.chapters;
      
      if (playerState.currentChapterIndex >= chapters.length - 1) {
        audio.currentTime = duration;
        // toast.info('At end of audiobook');
        return;
      }

      const overflow = 10 - timeRemaining;
      const targetChapterIndex = playerState.currentChapterIndex + 1;

      setCommandLoading(true);
      
      const wasPlaying = playerState.isPlaying;
      if (audio) {
        audio.pause();
        dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
        stopProgressTracking();
      }

      const jumpResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { 
          action: 'jump_to_chapter',
          chapter_index: targetChapterIndex
        }
      );

      const { session: updatedSession, chapter_index: returnedChapterIndex } = jumpResponse.data;
      setSession(updatedSession);

      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );

      const { audio_url } = playResponse.data;

      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks } = chunksResponse.data;

        if (chunks && chunks.length > 0) {
          const firstChunk = chunks[0];
          
          const chapter = outline?.chapters?.[returnedChapterIndex ?? targetChapterIndex];
          let chapterLabel = 'Chapter ' + (targetChapterIndex + 1);
          if (chapter) {
            // Only append title if it's different from label (avoid duplication)
            const cleanTitle = chapter.title?.replace(/^[\-–—:\.\s]+/, '');
            chapterLabel = (chapter.title && cleanTitle !== chapter.label)
              ? `${chapter.label} – ${cleanTitle}` 
              : chapter.label;
          }
          
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: null,
              chunks: chunks,
              chunkIndex: 0,
              chapterIndex: returnedChapterIndex ?? targetChapterIndex,
              chapterLabel: chapterLabel,
              charIndex: firstChunk.start_char,
              progress: 0,
              shouldPlay: false,
            }
          });

          audio.src = `${BACKEND_URL}${firstChunk.audio_url}`;

          const onLoadedMetadata = () => {
            audio.currentTime = Math.min(overflow, audio.duration || 0);
            
            if (wasPlaying) {
              audio.play().then(() => {
                dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                startProgressTracking();
              });
            }
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
          };

          audio.addEventListener('loadedmetadata', onLoadedMetadata);
          // toast.info(`Fast-forwarded to next chapter`);
        }
      }
    } catch (error) {
      console.error('Cross-chapter fast-forward failed:', error);
      // toast.error('Failed to fast-forward to next chapter');
    } finally {
      setCommandLoading(false);
    }
  };

  const jumpToChapter = async (chapterIndex) => {
    if (!manuscript || !outline || !session) return;
    
    try {
      // Use outline (single source of truth) instead of deprecated chapters_json
      const targetChapter = outline.chapters[chapterIndex];
      
      if (!targetChapter) {
        console.error(`Chapter ${chapterIndex} not found in outline`);
        return;
      }
      
      // Stop current playback
      const wasPlaying = playerState.isPlaying;
      if (audioRef.current) {
        audioRef.current.pause();
        dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
        stopProgressTracking();
      }
      
      setCommandLoading(true);
      setGeneratingAudio(true);
      
      // SAMPLE PREVIEW MODE: Use audio URL directly from sample manifest
      // Skip backend calls to avoid index misalignment
      if ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample && targetChapter?.audio_url) {
        console.log('[SAMPLE JUMP] Using direct audio URL from sample manifest');
        console.log('[SAMPLE JUMP] Target chapter index:', chapterIndex);
        console.log('[SAMPLE JUMP] Chapter title:', targetChapter.label || targetChapter.title);
        console.log('[SAMPLE JUMP] Original segment_index:', targetChapter.segment_index);
        console.log('[SAMPLE JUMP] Audio URL:', targetChapter.audio_url);
        
        // audio_url from manifest is either:
        // - /api/simple-ingest/jobs/{job_id}/audio/{segment_id} (new format)
        // - manuscripts/{id}/audio/segments/seg-XXXX.mp3 (legacy R2 path)
        const audioPath = targetChapter.audio_url.startsWith('/') 
          ? targetChapter.audio_url 
          : `/${targetChapter.audio_url}`;
        const audioSrc = `${BACKEND_URL}${audioPath}`;
        console.log('[SAMPLE JUMP] Full audio src:', audioSrc);
        
        const chapterLabel = (targetChapter.title && targetChapter.title !== targetChapter.label)
          ? `${targetChapter.label} – ${targetChapter.title}`
          : targetChapter.label || `Sample ${chapterIndex + 1}`;
        
        // Build synthetic chunk for player state compatibility
        const syntheticChunk = {
          audio_url: audioPath,
          start_char: 0,
          end_char: 0,
          duration: targetChapter.duration || 0
        };
        
        dispatch({
          type: 'SET_AUDIO_SOURCE',
          payload: {
            src: audioSrc,
            chunks: [syntheticChunk],
            chunkIndex: 0,
            chapterIndex: chapterIndex,
            chapterLabel: chapterLabel,
            charIndex: 0,
            progress: 0,
            shouldPlay: false,
          }
        });
        
        await playAudioNow(audioSrc);
        setGeneratingAudio(false);
        setCommandLoading(false);
        return;
      }
      
      // NORMAL PLAYBACK: Use backend jump_to_chapter + chunks endpoint
      // Use atomic jump_to_chapter command (0-based index)
      const jumpResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { 
          action: 'jump_to_chapter',
          chapter_index: chapterIndex // 0-based
        }
      );
      
      const { session: updatedSession, chapter_index: returnedChapterIndex } = jumpResponse.data;
      setSession(updatedSession);
      
      const playResponse = await axios.post(
        `${API}/sessions/${session.id}/command`,
        { action: 'play' }
      );
      
      const { audio_url } = playResponse.data;
      
      if (audio_url) {
        const chunksResponse = await axios.get(`${BACKEND_URL}${audio_url}`);
        const { chunks, _debug } = chunksResponse.data;
        
        // DIAGNOSTIC: Log exactly what we received
        console.log('[JUMP DEBUG] chunks response:', { 
          audio_url, 
          chunks_segment_id: chunks?.[0]?.segment_id,
          chunks_audio_url: chunks?.[0]?.audio_url,
          _debug 
        });
        
        if (chunks && chunks.length > 0) {
          const totalChars = manuscript.canonical_text?.length || 1;
          const newProgress = (chunks[0].start_char / totalChars) * 100;
          const audioSrc = `${BACKEND_URL}${chunks[0].audio_url}`;
          
          console.log('[JUMP DEBUG] Playing audioSrc:', audioSrc);
          
          // Only append title if it's different from label (avoid duplication)
          const chapterLabel = (targetChapter.title && targetChapter.title !== targetChapter.label)
            ? `${targetChapter.label} – ${targetChapter.title}`
            : targetChapter.label;
          
          dispatch({
            type: 'SET_AUDIO_SOURCE',
            payload: {
              src: audioSrc,
              chunks: chunks,
              chunkIndex: 0,
              chapterIndex: returnedChapterIndex ?? chapterIndex,
              chapterLabel: chapterLabel,
              charIndex: chunks[0].start_char,
              progress: newProgress,
              shouldPlay: false,
            }
          });
          
          await playAudioNow(audioSrc);
          // toast.success(`Now playing: ${chapterLabel}`);
        }
      }
      
      setGeneratingAudio(false);
    } catch (error) {
      console.error('Error jumping to chapter:', error);
      // toast.error('Failed to jump to chapter');
      setGeneratingAudio(false);
    } finally {
      setCommandLoading(false);
    }
  };

  // Handle restarting the audiobook from the beginning
  const handleRestartFromBeginning = async () => {
    setShowCompletionModal(false);
    
    if (!outline || !outline.chapters || outline.chapters.length === 0) {
      // toast.error('No chapters available');
      return;
    }
    
    // Jump to first chapter (index 0)
    await jumpToChapter(0);
    // toast.success('Restarting from the beginning');
  };

  // Handle closing the player after completion
  const handleCloseAfterCompletion = async () => {
    setShowCompletionModal(false);
    
    try {
      // Save position and close session
      await executeCommand('close');
    } catch (error) {
      console.error('Error closing session:', error);
      // Navigate home anyway
      navigate(returnTo);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Smartphone Frame */}
      <div 
        ref={playerContainerRef}
        tabIndex={-1}
        className="relative w-full max-w-[400px] mx-auto outline-none"
      >
        {/* Phone Bezel - 3D Effect */}
        <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl -z-10 
                        before:absolute before:inset-[3px] before:rounded-[2.85rem] before:bg-gradient-to-br before:from-slate-950 before:to-slate-900"></div>
        
        {/* Screen Container */}
        <div className="relative rounded-[2.85rem] overflow-hidden bg-gradient-to-br from-slate-900 to-slate-950 shadow-inner p-[3px]">
          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-950 rounded-b-3xl z-50 shadow-lg"></div>
          
          {/* Screen Content */}
          <div className="relative rounded-[2.7rem] overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-[812px] max-h-[812px] flex flex-col">
            
            {/* Mobile Autoplay Prompt - Shows when browser blocks autoplay */}
            {showMobilePlayPrompt && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-[2.7rem]">
                <div className="text-center p-6">
                  <div className="mb-4">
                    <img 
                      src="/newaudia-logo.jpg" 
                      alt="NewAudia" 
                      className="w-20 h-20 mx-auto rounded-xl shadow-lg"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Tap to Start Listening</h3>
                  <p className="text-sm text-slate-400 mb-6">Your browser requires a tap to enable audio playback</p>
                  <button
                    onClick={async () => {
                      setShowMobilePlayPrompt(false);
                      pendingAutoplayRef.current = false;
                      try {
                        await executeCommand('play');
                      } catch (error) {
                        console.error('[MOBILE PLAY] Error starting playback:', error);
                      }
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-full font-semibold text-lg shadow-lg shadow-purple-900/40 transition-all flex items-center gap-3 mx-auto"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Play Now
                  </button>
                </div>
              </div>
            )}
            
        {/* Main Card */}
        <Card className="flex-1 rounded-none border-0 bg-transparent shadow-none overflow-hidden flex flex-col">
          {/* Cover Image Header - Compact with cache-busting + click-to-zoom */}
          <div className="w-full h-52 bg-black overflow-hidden flex items-center justify-center p-2 flex-shrink-0 pt-6">
            <PlayerCoverZoom
              coverUrl={manuscript?.cover_image_path
                ? `${API}/covers/${manuscript.id}?v=${manuscript.cover_updated_at || manuscript.updated_at || Date.now()}`
                : '/newaudia-logo.jpg'}
              altText={`${manuscript?.title || 'Book'} cover`}
            />
          </div>

          {/* ASK AUDIA Button */}
          <div className="w-full px-3 pt-2 bg-gradient-to-br from-slate-900 to-slate-800">
            <button
              onClick={async () => {
                // SAMPLE PREVIEW MODE (author preview only, not guest): Button is disabled, do nothing
                if (isSamplePreviewMode && !isGuestSampleMode) {
                  return;
                }
                
                // Save hard bookmark and FULLY STOP playback before opening chat
                if (audioRef.current) {
                  const currentAudioTime = audioRef.current.currentTime || 0;
                  
                  // Stop playback immediately
                  audioRef.current.pause();
                  
                  // Clear the source to abort any ongoing fetch/buffering
                  const currentSrc = audioRef.current.src;
                  audioRef.current.src = '';
                  audioRef.current.load(); // Force abort of any pending requests
                  
                  // Restore src for later resume (but don't play)
                  if (currentSrc) {
                    audioRef.current.src = currentSrc;
                  }
                  
                  dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
                  
                  if (currentAudioTime > 0) {
                    await saveHardBookmark('pause', currentAudioTime);
                    console.log('[AUDIA CHAT] Stopped playback and saved bookmark at:', currentAudioTime);
                  }
                }
                setIsAudiaChatOpen(true);
              }}
              disabled={loading || generatingAudio || commandLoading || recapLoading || (isSamplePreviewMode && !isGuestSampleMode)}
              className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg shadow-lg transition-all font-semibold text-sm border ${
                (isSamplePreviewMode && !isGuestSampleMode)
                  ? 'bg-slate-700 text-slate-500 border-slate-600 cursor-not-allowed opacity-50'
                  : loading || generatingAudio || commandLoading || recapLoading
                    ? 'bg-slate-600 text-slate-400 border-slate-500 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-cyan-400/30'
              }`}
              title={
                (isSamplePreviewMode && !isGuestSampleMode)
                  ? 'Ask Audia enabled for Registered Readers Only'
                  : loading || generatingAudio || commandLoading || recapLoading 
                    ? 'Please wait while content is loading...' 
                    : 'Ask Audia about this book'
              }
            >
              <img 
                src="/newaudia-symbol.jpg" 
                alt="NewAudia" 
                className={`w-5 h-5 object-contain ${(isSamplePreviewMode && !isGuestSampleMode) ? 'opacity-50' : ''}`}
              />
              <span>{loading || generatingAudio || commandLoading || recapLoading ? 'Loading...' : 'ASK AUDIA'}</span>
              <img 
                src="/newaudia-symbol.jpg" 
                alt="NewAudia" 
                className={`w-5 h-5 object-contain ${(isSamplePreviewMode && !isGuestSampleMode) ? 'opacity-50' : ''}`}
              />
            </button>
          </div>
          
          {/* Book Title Display - Below Ask Audia */}
          {manuscript && (
            <div className="w-full px-3 py-2 bg-gradient-to-br from-slate-900 to-slate-800 text-center">
              <h2 className="text-lg font-bold text-white leading-tight">
                {manuscript.title}
              </h2>
              {manuscript.subtitle && (
                <p className="text-sm text-slate-300 mt-0.5">{manuscript.subtitle}</p>
              )}
            </div>
          )}
          
          {/* PREVIEW MODE INDICATOR - Below book title, above chapter navigation */}
          {(isPreviewMode || isPromotionalMode || isSamplePreviewMode || isGuestSampleMode) && (
            <div className="w-full flex justify-center px-3 py-1.5 bg-gradient-to-br from-slate-900 to-slate-800">
              <div className="px-3 py-1 bg-amber-600/80 text-white text-xs font-medium rounded-full flex items-center gap-1.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {isPromotionalMode 
                  ? 'Free Preview'
                  : (isSamplePreviewMode || isGuestSampleMode) 
                    ? 'Sample Preview'
                    : 'Preview Mode'
                }
              </div>
            </div>
          )}
          
          <CardContent className="space-y-1.5 pt-2 px-3 pb-2 bg-gradient-to-br from-slate-900 to-slate-800 flex-1 flex flex-col overflow-hidden">
            {/* Chapter Navigation - Compact */}
            <div className="grid grid-cols-3 gap-1.5">
              <button
                data-testid="prev-chapter-button"
                onClick={goToPrevChapter}
                disabled={generatingAudio || ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample && (playerState.currentChapterIndex ?? 0) <= 0)}
                className="bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 text-white px-2 py-1.5 rounded-lg shadow-md hover:from-slate-600 hover:to-slate-700 disabled:opacity-30 flex items-center justify-center gap-1 text-xs font-medium transition-all"
              >
                <SkipBack className="w-3 h-3" />
                <span>Prev</span>
              </button>
              
              {/* Chapter List Hamburger Menu */}
              <button
                data-testid="chapter-menu-button"
                onClick={() => setShowChapterMenu(true)}
                disabled={commandLoading || generatingAudio || !manuscript}
                className="bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 text-white px-2 py-1.5 rounded-lg shadow-md hover:from-slate-600 hover:to-slate-700 disabled:opacity-30 flex items-center justify-center gap-1 text-xs font-medium transition-all"
                title="View Chapters"
              >
                <Menu className="w-4 h-4" />
                <span>Chapters</span>
              </button>
              
              <button
                data-testid="next-chapter-button"
                onClick={goToNextChapter}
                disabled={generatingAudio || ((isSamplePreviewMode || isGuestSampleMode) && outline?.is_sample && (playerState.currentChapterIndex ?? 0) >= (outline.chapters?.length || 1) - 1)}
                className="bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 text-white px-2 py-1.5 rounded-lg shadow-md hover:from-slate-600 hover:to-slate-700 disabled:opacity-30 flex items-center justify-center gap-1 text-xs font-medium transition-all"
              >
                <span>Next</span>
                <SkipForward className="w-3 h-3" />
              </button>
            </div>

            {/* Playback Controls - Compact */}
            <div className="flex items-center justify-center gap-1.5">
              {/* Rewind Button */}
              <button
                data-testid="rewind-button"
                onClick={handleRewind}
                disabled={!playerState.isPlaying}
                className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-slate-600 shadow-lg hover:from-slate-600 hover:to-slate-700 disabled:opacity-30 transition-all active:scale-95"
                title="Rewind 10 seconds"
              >
                <RotateCcw className="w-4 h-4 text-white mx-auto" />
              </button>

              {/* Play/Pause Button */}
              {!playerState.isPlaying ? (
                <button
                  data-testid="play-button"
                  onClick={() => {
                    // If audio source exists, just resume playback (after pause in same session)
                    if (playerState.currentAudioSrc && audioRef.current?.src) {
                      audioRef.current.play();
                      dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                    } else {
                      // No audio loaded yet, need to fetch from backend
                      executeCommand('play');
                    }
                  }}
                  disabled={commandLoading || generatingAudio}
                  className={`w-14 h-14 rounded-full shadow-2xl transition-all active:scale-95 border-2 bg-gradient-to-br from-emerald-600 to-teal-600 border-emerald-500 hover:from-emerald-500 hover:to-teal-500 ${generatingAudio ? 'opacity-50' : ''}`}
                >
                  {generatingAudio ? (
                    <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" />
                  ) : (
                    <Play className="w-6 h-6 text-white mx-auto ml-1" />
                  )}
                </button>
              ) : (
                <button
                  data-testid="pause-button"
                  onClick={() => executeCommand('pause')}
                  disabled={commandLoading}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 border-2 border-purple-500 shadow-2xl hover:from-purple-500 hover:to-blue-500 transition-all active:scale-95"
                >
                  <Pause className="w-6 h-6 text-white mx-auto" />
                </button>
              )}

              {/* Fast Forward Button */}
              <button
                data-testid="ff-button"
                onClick={handleFastForward}
                disabled={!playerState.isPlaying}
                className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-slate-600 shadow-lg hover:from-slate-600 hover:to-slate-700 disabled:opacity-30 transition-all active:scale-95"
                title="Fast-forward 10 seconds"
              >
                <RotateCw className="w-4 h-4 text-white mx-auto" />
              </button>
            </div>

            {/* Now Playing - Compact */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-2 shadow-xl border border-slate-700/50 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-xs font-semibold text-purple-400 mb-0.5 uppercase tracking-wider">Now Playing</div>
                <div className="text-sm font-bold text-white leading-tight min-h-[1.5rem] flex items-center justify-center px-2">{playerState.currentChapter}</div>
              </div>
            </div>

            {/* Book Rating */}
            {manuscript?.id && user?.id && (
              <BookRating
                manuscriptId={manuscript.id}
                userId={user.id}
              />
            )}

            {/* Close Button */}
            <button
              data-testid="close-button"
              onClick={() => {
                // In guest sample mode OR promotional mode, show registration CTA instead of just closing
                if (isGuestSampleMode || isPromotionalMode) {
                  // Stop audio playback before showing the modal
                  if (audioRef.current) {
                    audioRef.current.pause();
                  }
                  dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
                  setShowGuestSampleCTA(true);
                } else {
                  executeCommand('close');
                }
              }}
              disabled={commandLoading}
              className="w-full flex items-center justify-center gap-2 py-1.5 bg-gradient-to-br from-red-600 to-red-700 text-white border border-red-500 rounded-lg shadow-lg hover:from-red-500 hover:to-red-600 transition-all text-sm font-semibold"
            >
              <X className="w-4 h-4" />
              Close This Book
            </button>

            {/* Manuscript Metadata & Info */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-2 space-y-1 border border-slate-700/50 shadow-xl">
              <div>
                <h3 className="text-sm font-bold text-white mb-0.5 leading-tight">
                  {manuscript?.title}
                </h3>
                {manuscript?.subtitle && (
                  <p className="text-xs text-slate-300 mb-0.5 leading-tight">{manuscript.subtitle}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="font-medium text-slate-200">{manuscript?.author}</span>
                  {manuscript?.publisher && (
                    <span className="text-slate-400">• {manuscript.publisher}</span>
                  )}
                </div>
              </div>
              
              {/* NewAudia Logo - Horizontal */}
              <div className="flex flex-col items-center pt-2 border-t border-slate-700/50">
                <img 
                  src="/newaudia-logo.png" 
                  alt="NewAudia" 
                  className="h-12 w-auto opacity-60"
                />
                {user && !isSamplePreviewMode && !isGuestSampleMode && !isPreviewMode && (
                  <button
                    data-testid="share-with-friend-btn"
                    onClick={async () => {
                      wasPlayingBeforeReferral.current = playerState.isPlaying;
                      if (playerState.isPlaying && audioRef.current) {
                        const audioTime = audioRef.current.currentTime;
                        audioRef.current.pause();
                        dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: false } });
                        await saveHardBookmark('pause', audioTime);
                      }
                      setReferralEmail('');
                      setReferralError('');
                      setShowReferralInput(true);
                    }}
                    className="mt-2 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium text-amber-300 border border-amber-500/30 hover:bg-amber-500/10 transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Share With A Friend
                  </button>
                )}
              </div>

            </div>

            {/* Hidden Audio Element - P4.1 Instrumented */}
            <audio
              ref={audioRef}
              onEnded={handleAudioEnded}
              onError={handleAudioError}
              onTimeUpdate={handleTimeUpdate}
              onCanPlayThrough={() => {
                perfTracker.markCanPlay();
                console.log('[PERF] canplaythrough event fired');
              }}
              onPlaying={() => {
                const metrics = perfTracker.markPlaying();
                console.log('[PERF] playing event fired, TTFP:', metrics?.TTFP?.toFixed(0), 'ms');
                
                // P6: Reset error retry count on successful playback
                resetAudioErrorRetries();
                
                // P4.2: Trigger prefetch of next chunk when playback starts
                if (manuscript?.id && playerState.audioChunks?.length > 0) {
                  prefetchNextChunk(
                    playerState.audioChunks,
                    playerState.currentChunkIndex,
                    manuscript.id,
                    playerState.currentChapterIndex
                  );
                }
              }}
              style={{ display: 'none' }}
            />
          </CardContent>
        </Card>
          </div>
        </div>
      </div>

      {/* Chapter Menu Dialog */}
      <AlertDialog open={showChapterMenu} onOpenChange={setShowChapterMenu}>
        <AlertDialogContent className="max-w-md bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-white">
              Chapters
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Select a chapter to jump directly to that section of the audiobook.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {/* Chapter list - use outline.chapters as primary source, audioManifest only for enhanced info */}
            {outline && outline.chapters && outline.chapters.length > 0 ? (
              <div className="space-y-2">
                {outline.chapters.map((chapter, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setShowChapterMenu(false);
                      jumpToChapter(index);
                    }}
                    disabled={commandLoading || generatingAudio}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      playerState.currentChapterIndex === index
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg border border-purple-500'
                        : 'bg-slate-800/50 hover:bg-slate-700/50 text-slate-200 border border-slate-700/50'
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{chapter.label || chapter.title}</span>
                        {playerState.currentChapterIndex === index && (
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                            Playing
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">No chapters available</p>
            )}
          </div>
          
          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t border-slate-700 mt-4">
            <button
              type="button"
              onClick={() => setShowChapterMenu(false)}
              className="px-6 py-2 bg-slate-700 border border-slate-600 text-white rounded-md hover:bg-slate-600 transition-colors font-medium cursor-pointer"
              data-testid="close-chapter-menu-button"
            >
              Close
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Book Completion Modal */}
      <AlertDialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-bold text-center text-teal-700 mb-2">
              🎉 Congratulations!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-lg text-gray-700">
              You've completed <span className="font-semibold text-gray-900">{manuscript?.title}</span>!
              <br />
              <span className="text-base text-gray-600 mt-2 block">
                Thank you for listening to this audiobook.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex flex-col gap-3 mt-6">
            <button
              type="button"
              onClick={handleRestartFromBeginning}
              disabled={commandLoading || generatingAudio}
              className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="restart-button"
            >
              🔄 Start Again from Beginning
            </button>
            
            <button
              type="button"
              onClick={handleCloseAfterCompletion}
              disabled={commandLoading}
              className="w-full px-6 py-3 bg-gray-200 border-2 border-gray-400 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-lg text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="close-completion-button"
            >
              ✕ Close & Return to Library
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Radio Mode - Next Track Modal */}
      {showRadioNextModal && (
        <AlertDialog open={showRadioNextModal}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <div className="flex justify-center mb-4">
                <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-12 object-contain" />
              </div>
              <AlertDialogTitle className="text-2xl font-bold text-center text-gray-900 mb-2">
                Ready for the next sample?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center text-lg text-gray-600">
                Keep listening to discover more titles in the NewAudia library.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-3 mt-6">
              <button
                type="button"
                data-testid="radio-next-yes-btn"
                onClick={() => {
                  setShowRadioNextModal(false);
                  navigate(returnTo, { state: { radioAdvance: true } });
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-lg transition-all shadow-lg font-semibold text-lg"
              >
                Yes, keep listening
              </button>
              <button
                type="button"
                data-testid="radio-next-no-btn"
                onClick={() => {
                  setShowRadioNextModal(false);
                  setShowGuestSampleCTA(true);
                }}
                className="w-full px-6 py-3 bg-gray-100 border-2 border-gray-300 rounded-lg hover:bg-gray-200 transition-colors font-semibold text-lg text-gray-700"
              >
                No thanks
              </button>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Guest Sample Completion CTA - Shown when unauthenticated user finishes sample or clicks Close */}
      <AlertDialog open={showGuestSampleCTA} onOpenChange={setShowGuestSampleCTA}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="text-2xl font-bold text-center text-gray-900 mb-2">
              Create Your Free Account
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-lg text-gray-600">
              Sign up to keep listening to <span className="font-semibold text-gray-800">{manuscript?.title}</span> and explore our full library of interactive audiobooks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex flex-col gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setShowGuestSampleCTA(false);
                setShowRegistrationModal(true);
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg transition-all shadow-lg font-semibold text-lg"
              data-testid="guest-cta-register-btn"
            >
              Create Free Account
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/public/library')}
              className="w-full px-6 py-3 bg-gray-100 border-2 border-gray-300 rounded-lg hover:bg-gray-200 transition-colors font-semibold text-lg text-gray-700"
              data-testid="guest-cta-browse-btn"
            >
              Browse Library
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Registration Modal - Full registration form */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => {
          setShowRegistrationModal(false);
          setRegistrationPromoCode(null);
        }}
        onSuccess={() => {
          setShowRegistrationModal(false);
          setRegistrationPromoCode(null);
          // After successful registration, redirect to reader library
          navigate('/reader/my-library');
        }}
        promoCode={registrationPromoCode}
      />

      {/* Resume from Bookmark Modal */}
      <AlertDialog open={showResumeModal} onOpenChange={setShowResumeModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo at Top */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="flex items-center justify-center gap-2 text-xl">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Bookmark Found
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p className="text-gray-700 text-center">
                We found your saved position from your last session. Would you like to resume where you left off?
              </p>
              
              {loadedBookmarkDetails && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-semibold text-gray-700">Manuscript:</span>
                    <span className="text-sm text-gray-900 text-right flex-1 ml-2">{loadedBookmarkDetails.manuscriptTitle}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-semibold text-gray-700">Chapter:</span>
                    <span className="text-sm text-gray-900 text-right flex-1 ml-2">{loadedBookmarkDetails.chapter}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-semibold text-gray-700">Segment:</span>
                    <span className="text-sm text-gray-900">{loadedBookmarkDetails.chunkIndex + 1}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-semibold text-gray-700">Time:</span>
                    <span className="text-sm text-gray-900">{loadedBookmarkDetails.timeFormatted}</span>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 italic text-center">
                💡 Resume to play, Start Over to begin fresh, or Cancel to just advance position
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button
              onClick={async () => {
                // Clear bookmark and start from beginning with auto-play
                setResumeTimeSeconds(null);
                setShowResumeModal(false);
                setLoadedBookmarkDetails(null);
                // toast.info('Starting from beginning');
                
                // Jump to first chapter and auto-play
                await jumpToChapter(0);
              }}
              className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold text-sm"
            >
              Start Over
            </button>
            <button
              onClick={async () => {
                // "Just Keep Reading" - resume playback from bookmark with autoplay (no recap)
                setShowResumeModal(false);
                // toast.info('Resuming from bookmark');
                
                console.log('[RESUME] Just Keep Reading - resuming with autoplay, no recap');
                // Resume playback from saved position with autoplay
                await resumeFromBookmark();
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
            >
              Just Keep Reading
            </button>
            <button
              onClick={async () => {
                // Close Resume modal
                setShowResumeModal(false);
                
                console.log('[RESUME] Resume button clicked - checking recap eligibility');
                
                // Check if user is eligible for recap (not creator/admin, and not at start of book)
                const shouldOfferRecap = await checkRecapEligibility(loadedBookmarkDetails);
                
                if (shouldOfferRecap) {
                  // Show "Get Caught Up" modal
                  console.log('[RESUME] User is eligible for recap - showing Get Caught Up modal');
                  // Modal will handle resume with autoplay after recap finishes or is skipped
                  // Note: showRecapPrompt is already set to true by checkRecapEligibility
                } else {
                  // No recap needed - just resume with autoplay
                  console.log('[RESUME] No recap needed - resuming with autoplay');
                  // toast.info('Resuming from bookmark');
                  await resumeFromBookmark();
                }
              }}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
            >
              Resume
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Referral Input Modal */}
      {showReferralInput && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 max-w-md w-full shadow-2xl p-6">
            <div className="flex justify-center mb-4">
              <img src="/newaudia-logo.png" alt="NewAudia" className="h-10 w-auto opacity-70" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-1" data-testid="referral-input-headline">
              So glad you're enjoying {manuscript?.title}.
            </h3>
            <p className="text-sm text-slate-400 mb-5">
              Enter the email address you'd like to share this with.
            </p>
            <input
              type="email"
              value={referralEmail}
              onChange={(e) => { setReferralEmail(e.target.value); setReferralError(''); }}
              placeholder="friend@example.com"
              data-testid="referral-email-input"
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-slate-950 border border-slate-700 text-white placeholder-slate-500 outline-none focus:border-amber-500 transition-colors"
              autoFocus
            />
            {referralError && (
              <p className="mt-2 text-xs text-red-400" data-testid="referral-error">{referralError}</p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => {
                  setShowReferralInput(false);
                  if (wasPlayingBeforeReferral.current && audioRef.current) {
                    audioRef.current.play();
                    dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                  }
                }}
                data-testid="referral-cancel-btn"
                className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!referralEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referralEmail)) {
                    setReferralError('Please enter a valid email address.');
                    return;
                  }
                  setReferralLoading(true);
                  setReferralError('');
                  try {
                    const token = localStorage.getItem('accessToken');
                    const res = await fetch(`${BACKEND_URL}/api/referrals/send`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ manuscript_id: manuscript?.id, referred_email: referralEmail }),
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.detail || 'Failed to send referral.');
                    }
                    setShowReferralInput(false);
                    setShowReferralConfirm(true);
                  } catch (e) {
                    setReferralError(e.message);
                  } finally {
                    setReferralLoading(false);
                  }
                }}
                disabled={referralLoading}
                data-testid="referral-send-btn"
                className="px-5 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {referralLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referral Confirmation Modal */}
      {showReferralConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 max-w-md w-full shadow-2xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <img src="/newaudia-logo.png" alt="NewAudia" className="h-10 w-auto opacity-70" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2" data-testid="referral-confirm-headline">
              Thank You for Sharing!
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              We've sent your recommendation. Welcome back — pick up right where you left off with {manuscript?.title}.
            </p>
            <button
              onClick={() => {
                setShowReferralConfirm(false);
                if (wasPlayingBeforeReferral.current && audioRef.current) {
                  audioRef.current.play();
                  dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                }
              }}
              data-testid="referral-okay-btn"
              className="mt-5 px-6 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-500 transition-colors"
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* Audia Chat Modal */}
      {/* Catch Me Up Recap Modal */}
      {showRecapPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 max-w-lg w-full shadow-2xl">
            {/* NewAudia Logo at Top */}
            <div className="flex justify-center pt-6 pb-4">
              <img 
                src="/newaudia-logo.png" 
                alt="NewAudia" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {/* Header with Audia Avatar */}
            <div className="px-6 pb-6 border-b border-slate-700">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img 
                  src="/audia-avatar.jpg" 
                  alt="Audia" 
                  className="h-16 w-16 rounded-full object-cover border-2 border-cyan-400 shadow-lg"
                />
                <div className="text-center">
                  <h2 className="text-xl font-bold text-cyan-400 leading-tight">
                    {recapText 
                      ? `Here's your recap of ${manuscript?.title || 'your book'}` 
                      : `Want a quick catch-up on ${manuscript?.title || 'your book'}?`
                    }
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">from Audia</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {recapLoading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-200 font-semibold text-lg">Preparing your recap...</p>
                  <p className="text-slate-400 text-sm mt-2">Audia is reviewing where you left off</p>
                </div>
              ) : recapText ? (
                <div className="space-y-4">
                  <div className="bg-slate-800/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{recapText}</p>
                  </div>
                  {recapAudioUrl ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-green-400">
                      <Volume2 className="w-4 h-4" />
                      <span>Audio playing...</span>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center italic">
                      Click "Read Out Loud" to hear this recap, or "Resume" to continue reading.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-300 text-center">
                    It looks like it's been a little while since you last listened to this book. 
                    Would you like Audia to catch you up on what happened before your last position?
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-700 space-y-2">
              {recapLoading ? (
                <button
                  disabled
                  className="w-full px-6 py-3 bg-slate-700 text-slate-400 rounded-xl font-semibold cursor-not-allowed"
                >
                  Generating recap...
                </button>
              ) : recapText ? (
                <div className="space-y-2">
                  {!recapAudioUrl && (
                    <button
                      onClick={generateRecapTTS}
                      className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <Volume2 className="w-5 h-5" />
                      Read Out Loud
                    </button>
                  )}
                  <button
                    onClick={handleSkipRecap}
                    className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold transition-colors"
                  >
                    {recapAudioUrl ? 'Continue to Book' : 'Resume'}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={generateRecap}
                    className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold transition-colors"
                  >
                    Yes, catch me up
                  </button>
                  <button
                    onClick={handleSkipRecap}
                    className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl font-semibold transition-colors"
                  >
                    No, just play
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Conversion Bubble (guest sample mode only) */}
      {isGuestSampleMode && showConversionBubble && (
        <div
          data-testid="preview-conversion-bubble"
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 max-w-sm w-[90vw] animate-in fade-in slide-in-from-bottom-4 duration-500"
        >
          <button
            onClick={() => {
              setShowConversionBubble(false);
              // Load audio from backend and start playback
              executeCommand('play');
            }}
            data-testid="preview-bubble-dismiss"
            className="w-full px-5 py-4 rounded-2xl shadow-2xl border border-teal-500/30 bg-gradient-to-br from-[#1B3A6B] to-[#0f2847] text-left hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer"
          >
            <p className="text-white text-sm font-semibold leading-relaxed">
              Ask Audia 3 Questions To Unlock <span className="text-[#00A896]">25 Free Reader Credits</span> When You Register Today!
            </p>
            <p className="text-teal-400/70 text-lg mt-1.5">Tap to start listening</p>
          </button>
        </div>
      )}

      <AudiaChatModal
        isOpen={isAudiaChatOpen}
        onClose={() => {
          setIsAudiaChatOpen(false);
          
          // Return focus to player container
          setTimeout(() => {
            if (playerContainerRef.current) {
              playerContainerRef.current.focus();
              console.log('[AUDIA CHAT] Focus returned to player');
            }
          }, 100);
          
          // Auto-resume playback from bookmark after closing chat
          if (audioRef.current && manuscript?.id) {
            // Get user-specific bookmark key
            const token = localStorage.getItem('accessToken');
            let userId = 'anonymous';
            if (token) {
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                userId = payload.user_id || payload.sub || 'anonymous';
              } catch (e) {
                console.warn('[BOOKMARK] Could not extract user ID from token');
              }
            }
            
            const key = `newaudia_bookmark_${userId}_${manuscript.id}`;
            const raw = localStorage.getItem(key);
            
            if (raw) {
              try {
                const bookmark = JSON.parse(raw);
                console.log('[AUDIA CHAT] Restoring playback from bookmark:', bookmark.positionSeconds);
                audioRef.current.currentTime = bookmark.positionSeconds;
                audioRef.current.play().then(() => {
                  dispatch({ type: 'UPDATE_PLAYBACK_STATE', payload: { isPlaying: true } });
                  console.log('[AUDIA CHAT] Resumed playback after closing chat');
                }).catch(err => {
                  console.error('[AUDIA CHAT] Error resuming playback:', err);
                });
              } catch (err) {
                console.error('[AUDIA CHAT] Error parsing bookmark:', err);
              }
            }
          }
        }}
        manuscriptId={manuscript?.id}
        manuscriptTitle={manuscript?.title || 'this book'}
        currentChapterContext={{
          chapterIndex: playerState.currentChapterIndex,
          chapterLabel: playerState.currentChapter,
          totalChapters: outline?.chapters?.length || 0,
          chapterTitle: outline?.chapters?.[playerState.currentChapterIndex]?.label || playerState.currentChapter
        }}
        isGuestSampleMode={isGuestSampleMode}
        previewQuestionCount={previewQuestionCount}
        onPreviewQuestionAsked={() => setPreviewQuestionCount(prev => Math.min(prev + 1, 3))}
        onPreviewContinue={() => {
          setIsAudiaChatOpen(false);
          setRegistrationPromoCode(PREVIEW_PROMO_CODE);
          setShowRegistrationModal(true);
        }}
      />

    </div>
  );
};

export default ManuscriptPlayer;
